/* eslint-disable */
import { unwrapResult } from '@reduxjs/toolkit';
import { Skeleton } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  Notification,
  Row,
  Space,
  Spacing,
  SyncOutlined,
  Text,
} from 'farmhub-ui-core';
import { unflatten } from 'flat';
import html2canvas from 'html2canvas';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { isEmpty } from 'lodash';

import {
  AddressData,
  GoogleApi,
  PricePolicyImportSettings,
  ProposalPolicyImportSettings,
} from 'api';
import {
  BoxColorIcon,
  CheckFilledIcon,
  CompleteIcon,
  FarmerColorIcon,
  IncognitoColorIcon,
  PendingConfirmIcon,
  PendingExecuteIcon,
  PendingSupplierConfirmIcon,
  PeopleLinkColorIcon,
  ProcessingIcon,
  RejectFilledIcon,
  ShopColorIcon,
  StopFilledIcon,
  TruckColorIcon,
  TruckIcon,
  UserColorIcon,
  VolunteerColorIcon,
  WaitingFilledIcon,
} from 'components/Icon';
import { ProductPriceUnit } from 'containers/ProductionManager/ProductManager/Product/components/Unit/commons';
import { env } from 'env';
import { theme } from 'styled';

import { REFRESH_TOKEN, SELECTED_ENTITY, USER_ID } from 'types';
import {
  ACTIVE_STATUS,
  BILL_TYPE,
  CELLPHONE_CHARS,
  CELLPHONE_REGEX,
  COLLABORATOR_TEXT,
  DAY_OF_WEEK,
  DEFAULT_ORDER_PAYMENT_METHOD,
  DEVICES,
  DOMAIN_STATUS,
  ENTITY_TYPE,
  IMPORT_TYPE_VALUE,
  ImageSize,
  LANDLINE_REGEX,
  ORDER_ACTION_ENUM,
  ORDER_ENTITY_ROLE,
  ORDER_PAYMENT_METHOD,
  ORDER_STATUS,
  ORDER_STATUS_TEXT,
  ORDER_TYPE,
  PARTNER_TYPE,
  POLICY_CODE,
  PRODUCT_RELATIVE_QUANTITY,
  PRODUCTION_PLAN_STATUS,
  REPORT_TYPE,
  VIETNAM_PHONE_REGEX,
  TIME_GROUP_TYPE,
  VN_DAY,
  INVITATION_TYPE,
  PARTNER_IN_CHAIN_TYPE,
  FLOW_CHAIN_UPDATE_STATUS,
  NextUpdateStatusChainOrder,
  CHAIN_ORDER_STATUS_TEXT,
  FLOW_UPDATE_STATUS,
  DISCOUNT_TYPE_TEXT,
  DISCOUNT_METHOD_TEXT,
  ADJUSTMENT_TYPE,
  STOCK_TYPE_TEXT,
  INVENTORY_AUDIT_STATUS,
  BILL_STATUS,
} from './constants';
import { PROMOTION_TYPE } from 'containers/SaleManager/Discount/Discount/commons';

export const checkOnlySpaces = (str: string) => str.trim().length === 0;

export const checkOnlyNumber = (str: string) => /^(?:\d*)$/.test(str);

export const checkFirstCharIsNotSpace = (str: string) => str.charAt(0) === ' ';

export const checkContainSpace = (str: string) => /\s/g.test(str);

export const formatTime = value => {
  const roundUpHour =
    value.minute() || value.second() || value.millisecond()
      ? value.add(1, 'hour').startOf('hour')
      : value.startOf('hour');

  return roundUpHour;
};

export const formatMoney = (number, thousandsSep = ',') => {
  if (!number || number === 0) return 0;
  // if (!number) return '';
  number = Math.round(number * 1000) / 1000;

  return (
    number &&
    number
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep)
      .replace(/-/g, '- ')
  );
};

export const formatWebsite = (website: string) => {
  if (!website) return '';

  return website.replace(/(^\w+:|^)\/\//, '');
};

export const formatQuantity = (
  number: number,
  decimalPlaces = 6,
  thousandsSep = ',',
  decimalSep = '.'
) => {
  if (!number || number === 0) {
    return 0;
  }
  const parts = roundProductQuantity(number, decimalPlaces)
    .toString()
    .split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
  return parts.join(decimalSep);
};

export const formatAppearPhoneNumber = (phoneNumber?: string) => {
  if (!phoneNumber) {
    return 'Chưa có liên hệ';
  }

  const cleaned = `${phoneNumber}`.replace(/\D/g, '').replace('84', '0');
  const cellPhoneMatch = cleaned.match(CELLPHONE_REGEX);
  const landlineMatch = cleaned.match(LANDLINE_REGEX);

  if (cellPhoneMatch) {
    // For Phone 10 number
    if (cellPhoneMatch[0].length === 10)
      return `${[
        cellPhoneMatch[0].slice(0, 4),
        cellPhoneMatch[0].slice(4, 7),
        cellPhoneMatch[0].slice(7, 10),
      ].join(` `)}`;

    // For Phone 11 number
    return `${[
      cellPhoneMatch[0].slice(0, 3),
      cellPhoneMatch[0].slice(3, 5),
      cellPhoneMatch[0].slice(5, 8),
      cellPhoneMatch[0].slice(8, 11),
    ].join(' ')}`;
  }

  if (landlineMatch) {
    return `${landlineMatch[1]} ${[
      landlineMatch[6].slice(0, 3),
      landlineMatch[6].slice(3, 6),
      landlineMatch[6].slice(6),
    ].join(' ')}`;
  }
  return phoneNumber;
};

export const detectTime = (createdAt: string | undefined) => {
  if (!createdAt) {
    return 'Trống';
  }
  const diffMinute = moment().diff(createdAt, 'm');
  if (diffMinute === 0) {
    return 'Mới gần đây';
  }
  if (diffMinute < 60) {
    return `${diffMinute} phút trước`;
  }
  if (diffMinute < 60 * 24) {
    return `${Math.floor(diffMinute / 60) + 1} giờ trước`;
  }
  if (diffMinute < 60 * 24 * 30) {
    return `${Math.floor(diffMinute / (60 * 24)) + 1} ngày trước`;
  }
  if (diffMinute < 60 * 24 * 30 * 12) {
    return `${Math.floor(diffMinute / (60 * 24 * 30)) + 1} tháng trước`;
  }
  return `${Math.floor(diffMinute / (60 * 24 * 30 * 12)) + 1} năm trước`;
};

export function removeVietnameseTones(str: string) {
  str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, 'a');
  str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, 'e');
  str = str.replace(/ì|í|ị|ỉ|ĩ/g, 'i');
  str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, 'o');
  str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, 'u');
  str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, 'y');
  str = str.replace(/đ/g, 'd');
  str = str.replace(/À|Á|Ạ|Ả|Ã|Â|Ầ|Ấ|Ậ|Ẩ|Ẫ|Ă|Ằ|Ắ|Ặ|Ẳ|Ẵ/g, 'A');
  str = str.replace(/È|É|Ẹ|Ẻ|Ẽ|Ê|Ề|Ế|Ệ|Ể|Ễ/g, 'E');
  str = str.replace(/Ì|Í|Ị|Ỉ|Ĩ/g, 'I');
  str = str.replace(/Ò|Ó|Ọ|Ỏ|Õ|Ô|Ồ|Ố|Ộ|Ổ|Ỗ|Ơ|Ờ|Ớ|Ợ|Ở|Ỡ/g, 'O');
  str = str.replace(/Ù|Ú|Ụ|Ủ|Ũ|Ư|Ừ|Ứ|Ự|Ử|Ữ/g, 'U');
  str = str.replace(/Ỳ|Ý|Ỵ|Ỷ|Ỹ/g, 'Y');
  str = str.replace(/Đ/g, 'D');
  // Some system encode vietnamese combining accent as individual utf-8 characters
  // Một vài bộ encode coi các dấu mũ, dấu chữ như một kí tự riêng biệt nên thêm hai dòng này
  str = str.replace(/\u0300|\u0301|\u0303|\u0309|\u0323/g, ''); // ̀ ́ ̃ ̉ ̣  huyền, sắc, ngã, hỏi, nặng
  str = str.replace(/\u02C6|\u0306|\u031B/g, ''); // ˆ ̆ ̛  Â, Ê, Ă, Ơ, Ư
  // Remove extra spaces
  // Bỏ các khoảng trắng liền nhau
  str = str.replace(/ + /g, ' ');
  str = str.trim();
  // Remove punctuations
  // Bỏ dấu câu, kí tự đặc biệt
  str = str.replace(
    /!|@|%|\^|\*|\(|\)|\+|\=|\<|\>|\?|\/|,|\.|\:|\;|\'|\"|\&|\#|\[|\]|~|\$|_|`|-|{|}|\||\\/g,
    ' '
  );
  return str;
}

export const detechTime = createdAt => {
  const diffMinute = moment().diff(createdAt, 'm');
  if (diffMinute === 0) {
    return 'Mới gần đây';
  }
  if (diffMinute < 60) {
    return `${diffMinute} phút trước`;
  }
  if (diffMinute < 60 * 24) {
    return `${Math.floor(diffMinute / 60) + 1} giờ trước`;
  }
  if (diffMinute < 60 * 24 * 30) {
    return `${Math.floor(diffMinute / (60 * 24)) + 1} ngày trước`;
  }
  if (diffMinute < 60 * 24 * 30 * 12) {
    return `${Math.floor(diffMinute / (60 * 24 * 30)) + 1} tháng trước`;
  }
  return `${Math.floor(diffMinute / (60 * 24 * 30 * 12)) + 1} năm trước`;
};

export const renderObjectType = type => {
  switch (type) {
    case 0:
      return <Text>Cây</Text>;
    case 1:
      return <Text>Luống</Text>;
    case 2:
      return <Text>Toàn bộ vùng</Text>;
    case 3:
      return <Text>Trang trại</Text>;
    case 4:
      return <Text>Nhà kính</Text>;
    case 5:
      return <Text>Khác</Text>;
    default:
      return '';
  }
};

export const isValidURL = str => {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
    '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
    '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
    '(\\#[-a-z\\d_]*)?$',
    'i'
  ); // fragment locator
  return !!pattern.test(str);
};

export const isVietnamesePhoneNumber = (value: string): boolean => {
  if (!value) {
    return false;
  }
  const phoneNumber = removeSpaceString(value);

  if (phoneNumber.length === 10 && phoneNumber[0] === '0') {
    return VIETNAM_PHONE_REGEX.test(phoneNumber);
  }

  // /([\+84|84|0]+(3|5|7|8|9|1[2|6|8|9]))+([0-9]{8})\b/.test(number); //eslint-disable-line
  return CELLPHONE_REGEX.test(phoneNumber) || LANDLINE_REGEX.test(phoneNumber);
};

export const formatPhoneNumber = (value: string): string => {
  if (!value) {
    return '';
  }

  let number = value.trim();
  number = removeSpaceString(number);
  switch (number[0]) {
    case '0':
      return number.replace('0', '+84');
    case '8':
      return `+${number}`;
    default:
      return number;
  }
};

export const validateEmail = email => {
  // const re =
  //   /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; //eslint-disable-line
  // return re.test(String(email).toLowerCase());
  const mail = email.trim();
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; //eslint-disable-line
  return re.test(String(mail).toLowerCase());
};

export const validateURL = str => {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
    '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
    '(\\:\\d+)?(\\/[@-a-z\\d%_.~+]*)*' + // port and path
    '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
    '(\\#[-a-z\\d_]*)?$',
    'i'
  ); // fragment locator
  return !!pattern.test(str);
};

export const getQueryString = ({
  search,
  field = '',
  fields = [] as string[],
  defaultValue = '',
  whiteList = [] as string[],
}): any => {
  const value: string = new URLSearchParams(search).get(field) || '';

  const fieldResults = {};
  if (fields.length > 0) {
    fields.forEach(key => {
      fieldResults[key] = new URLSearchParams(search).get(key) || '';
    });

    return fieldResults;
  }

  if (whiteList.length === 0) {
    return value || defaultValue;
  }
  if (whiteList.includes(value)) {
    return value;
  }

  return defaultValue;
};

export const truncate = (str: string, n: number) => {
  if (!str) {
    return ''
  }
  return str.length > n ? `${str.substr(0, n - 1)}...` : str;
}


export const truncateWords = (text: string, wordNumber: number) => {
  const wordList = text?.split(' ');
  return `${wordList?.slice(0, wordNumber).join(' ')}`;
};

export const parseUtils = (obj, key, type) => {
  if (obj[key] || obj[key] === 0) {
    obj[key] = parseType(type, obj[key]);
  }
};

const parseType = (type, value) => {
  switch (type) {
    case 'string': {
      if (value === 0) {
        return '0';
      }
      return value.toString();
    }

    case 'images':
      return (
        value &&
        value.map(data => {
          if (!data?.isVideo) {
            return {
              type: 0,
              url: data.url,
            };
          }
          return {
            type: 1,
            url: data[0].url[0],
          };
        })
      );

    case 'video':
      return (
        value && {
          type: 1,
          url: value[0]?.url[0],
        }
      );

    case 'url':
      return value && value.map(data => data.url);

    case 'image':
      return value && value[0]?.url[0];

    default:
      return value && parseInt(value, 0);
  }
};

export const parseGalleryImage = async (url: string) => {
  const img = new Image();
  img.src = url;
  await img.decode();
  return {
    src: url,
    width: img.width,
    height: img.height,
  };
};

export const removeEmptyElement = (body: any) => {
  if (body) {
    Object.keys(body).forEach(e => {
      if (body[e] === undefined || body[e] === null || body[e] === 'all') {
        delete body[e];
      } else if (typeof body[e] === 'object' && !Array.isArray(body[e])) {
        removeEmptyElement(body[e]);
      } else if (Array.isArray(body[e])) {
        removeEmptyElementFromObjectArray(body[e]);
      }
    });
  }
};

export const removeEmptyElementFromObjectArray = (arr: any[]) => {
  for (const item of arr) {
    if (item != null && typeof item === 'object' && !Array.isArray(item)) {
      removeEmptyElement(item);
    }
  }
};

export const removePropertyFromObject = (object: any, properties: string[]) => {
  if (typeof object === 'object' && !Array.isArray(object) && object != null) {
    properties.forEach(property => {
      delete object[property];
    });
  }
};

export const removePropertyFromObjectArray = (
  arr: any[],
  ...properties: string[]
) => {
  for (const item of arr) {
    removePropertyFromObject(item, properties);
  }
};

export const renderTableSkeleton = columns => {
  const skeleton = columns.map((column, index) => {
    let renderContent;

    switch (column.type) {
      case 'avatar':
        renderContent = (
          <Spacing>
            <Skeleton.Avatar
              shape="circle"
              style={{
                width: theme.avatar.size.md,
                height: theme.avatar.size.md,
              }}
            />
            <Skeleton.Input
              size="small"
              style={{ width: column.width }}
            />
          </Spacing>
        );
        break;
      case 'image':
        renderContent = (
          <Spacing>
            <Skeleton.Input
              size="small"
              style={{
                width: theme.avatar.size.md,
                height: theme.avatar.size.md,
                borderRadius: 6
              }}
            />
            <Skeleton.Input
              size="small"
              style={{ width: column.width }}
            />
          </Spacing>
        );
        break;
      case 'doubleInput':
        renderContent = (
          <Spacing direction="vertical">
            <Skeleton.Input
              size="small"
              style={{ width: column.width }}
            />
            <Skeleton.Input
              size="small"
              style={{ width: column.width }}
            />
          </Spacing>
        );
        break;
      case 'button':
        renderContent = <Skeleton.Button size="small" />;
        break;
      case 'banner':
        renderContent = (
          <Skeleton.Input style={{ width: column.width, height: 110 }} />
        );
        break;
      case 'quantityInput':
        renderContent = (
          <Row>
            <Skeleton.Input size="small" style={{ width: column.width, height: column.width, borderRadius: '4px 0px 0px 4px' }} />
            <Space width={1}></Space>
            <Skeleton.Input size="small" style={{ width: column.width + column.width, height: column.width }} />
            <Space width={1}></Space>
            <Skeleton.Input size="small" style={{ width: column.width, height: column.width, borderRadius: '0px 4px 4px 0px' }} />
          </Row>
        );
        break;
      default:
        renderContent = (
          <Skeleton.Input size="small" style={{ width: column.width }} />
        );
    }

    const result = {
      title: column.title,
      key: index.toString(),
      render: () => renderContent,
    };

    return column.width
      ? {
        ...result,
        width:
          column.type === 'image' && typeof column.width === 'number'
            ? column.width + 60
            : column.width,
      }
      : result;
  });

  return skeleton;
};

export const renderEmployeeRole = (role: number) => {
  switch (role) {
    case 0:
      return 'Nhân viên';
    case 1:
      return 'Quản lý';
    default:
      return 'Trống';
  }
};

export const getInvitationTitle = (type: number) => {
  switch (type) {
    case INVITATION_TYPE.CONNECT_ENTITY:
      return 'Liên kết thực thể';
    case INVITATION_TYPE.SYNC_PRODUCT:
      return 'Đồng bộ sản phẩm';
    case INVITATION_TYPE.SYNC_PRODUCT_DIARIES:
      return 'Liên kết nhật ký sản phẩm'
    default:
      return '';
  }
}

export const getEntityInfo = (type: number) => {
  switch (type) {
    case 1:
      return {
        name: 'Nông trại',
        icon: <FarmerColorIcon width={18} height={18} />,
      };
    case 2:
      return {
        name: 'Cửa hàng',
        icon: <ShopColorIcon width={18} height={18} />,
      };
    case 3:
      return {
        name: `${COLLABORATOR_TEXT.CAPITALIZE}`,
        icon: <PeopleLinkColorIcon width={18} height={18} />,
      };
    case 4:
      return {
        name: 'Kho chứa',
        icon: <BoxColorIcon width={18} height={18} />,
      };
    case 5:
      return {
        name: 'Vận chuyển',
        icon: <TruckColorIcon width={18} height={18} />,
      };
    case 6:
      return {
        name: 'Thiện nguyện',
        icon: <VolunteerColorIcon width={18} height={18} />,
      };
    case 7:
      return {
        name: 'Ẩn danh',
        icon: <IncognitoColorIcon width={18} height={18} />,
      };
    default:
      return {
        name: 'Người dùng',
        icon: <UserColorIcon width={18} height={18} />,
      };
  }
};

export const genEntityName = (type: number) => {
  switch (type) {
    case 0:
      return {
        vi: 'Người dùng',
        en: 'user',
      };
    case 1:
      return {
        vi: 'Nông trại',
        en: 'farm',
      };
    case 2:
      return {
        vi: 'Cửa hàng',
        en: 'store',
      };
    case 3:
      return {
        vi: `${COLLABORATOR_TEXT.CAPITALIZE}`,
        en: 'collaborator',
      };
    case 4:
      return {
        vi: 'Kho chứa',
        en: 'warehouse',
      };
    case 5:
      return {
        vi: 'Vận chuyển',
        en: 'transport',
      };
    case 6:
      return {
        vi: 'Thiện nguyện',
        en: 'volunteer',
      };
    case 7:
      return {
        vi: 'Ẩn danh',
        en: 'incognito',
      };
    default:
      return {
        vi: '',
        en: '',
      };
  }
};

export const reportQuantity = (reportInfo: {
  reportType: number;
  relativeQuantity: number;
  absoluteQuantity: number;
}): string => {
  const { reportType, relativeQuantity, absoluteQuantity } = reportInfo;
  switch (reportType) {
    case 0:
      switch (relativeQuantity) {
        case 0:
          return 'Hết hàng';
        case 1:
          return 'Có ít';
        case 2:
          return 'Có vừa';
        case 3:
          return 'Có nhiều';
        default:
          return '0';
      }
    case 1:
      return formatQuantity(roundProductQuantity(absoluteQuantity, 2)).toString();
    default:
      return '0';
  }
};

export const getRelativeQuantityColor = (reportInfo: any) => {
  const { reportType, relativeQuantity } = reportInfo;
  if (reportType === REPORT_TYPE.RELATIVE) {
    switch (relativeQuantity) {
      case 0:
        return theme.colors.text.red;
      case 1:
        return theme.colors.text.lightOrange;
      case 2:
        return theme.colors.text.orange;
      case 3:
        return theme.colors.text.green;
      default:
        return theme.colors.text.primary;
    }
  }
  return theme.colors.primary.main;
};

export const getPurchaseOrderDetailPathByStatus = (
  status: number,
  isOwner: boolean
) => {
  let rs = '';
  if (isOwner) {
    rs += 'customer-';
  } else {
    rs += 'collaborator-';
  }

  switch (status) {
    case ORDER_STATUS.PENDING:
      rs += 'pending';
      break;
    case ORDER_STATUS.PROCESSING:
      rs += 'processing';
      break;

    case ORDER_STATUS.DELIVERY:
      rs += 'delivery';
      break;

    case ORDER_STATUS.CUSTOMER_CONFIRM:
      rs += 'confirm-pending';
      break;

    case ORDER_STATUS.REJECT:
      rs += 'reject';
      break;

    case ORDER_STATUS.CUSTOMER_DRAFT:
      rs += 'draft';
      break;

    case ORDER_STATUS.COMPLETE:
      rs += 'completed';
      break;

    case ORDER_STATUS.PRE_PROCESSING:
      rs += 'pre-processing';
      break;

    case ORDER_STATUS.SUPPLIER_CONFIRM:
      rs += 'confirm-complete';
      break;

    case ORDER_STATUS.PENDING_EXECUTE:
      rs += 'waiting-execute-co-buy';
      break;

    case ORDER_STATUS.SUPPLIER_DRAFT:
      rs += 'supplier-draft';
      break;

    default:
      return '';
  }

  return rs;
};

export const getCouponStatus = (createdTime: string, endedTime: string) => {
  let status = 0;
  if (moment() >= moment(createdTime) && moment() <= moment(endedTime)) {
    status = 1;
  } else if (moment() >= moment(endedTime)) {
    status = 2;
  }

  switch (status) {
    case 0:
      return {
        name: 'Sắp diễn ra',
        color: 'warning',
        icon: <ClockCircleOutlined />,
      };
    case 1:
      return {
        name: 'Đang hoạt động',
        color: 'success',
        icon: <SyncOutlined />,
      };
    case 2:
      return {
        name: 'Đã kết thúc',
        color: 'default',
        icon: <CheckCircleOutlined />,
      };
    default:
      return { name: '', color: 'default', icon: <ClockCircleOutlined /> };
  }
};

export const getCobuyStatus = (
  status: number,
  startedAt: string,
  finishedAt: string
) => {
  // const finishedAtValue = finishedAt ? finishedAt : moment(startedAt).add(24, 'hours')
  switch (status) {
    case 0:
      return {
        name: 'Đang hoạt động',
        color: 'success',
        icon: <CheckCircleOutlined />,
      };
    case 1:
      return { name: 'Đã kết thúc', color: 'error', icon: <SyncOutlined /> };
    case 2:
      return { name: 'Dừng hoạt động', color: 'error', icon: <SyncOutlined /> };
    case 3:
      return {
        name: 'Sắp diễn ra',
        color: 'warning',
        icon: <ClockCircleOutlined />,
      };
    case 4:
      return {
        name: 'Chờ phê duyệt',
        color: 'default',
        icon: <ClockCircleOutlined />,
      };
    default:
      return {
        name: 'Không xác định',
        color: 'default',
        icon: <ClockCircleOutlined />,
      };
  }
};

export const genComboStatus = (status: number) => {
  switch (status) {
    case 0:
      return {
        name: 'Đang hoạt động',
        color: 'success',
        icon: <CheckCircleOutlined />,
      };
    case 1:
      return { name: 'Đã kết thúc', color: 'error', icon: <SyncOutlined /> };
    case 2:
      return {
        name: 'Sắp diễn ra',
        color: 'warning',
        icon: <ClockCircleOutlined />,
      };
    default:
      return {
        name: 'Không xác định',
        color: 'default',
        icon: <ClockCircleOutlined />,
      };
  }
};

export const genDiscountStatus = (status: number) => {
  switch (status) {
    case 0:
      return {
        name: 'Đang hoạt động',
        color: 'success',
        icon: <CheckCircleOutlined />,
      };
    case 1:
      return { name: 'Đã kết thúc', color: 'error', icon: <SyncOutlined /> };
    case 2:
      return {
        name: 'Sắp diễn ra',
        color: 'warning',
        icon: <ClockCircleOutlined />,
      };
    default:
      return {
        name: 'Không xác định',
        color: 'default',
        icon: <ClockCircleOutlined />,
      };
  }
};

export const genCouponStatus = (status: number) => {
  switch (status) {
    case 0:
      return {
        name: 'Đang hoạt động',
        color: 'success',
        icon: <CheckCircleOutlined color={theme.colors.success} />,
      };
    case 1:
      return { name: 'Đã kết thúc', color: 'error', icon: <SyncOutlined /> };
    case 2:
      return {
        name: 'Sắp diễn ra',
        color: 'warning',
        icon: <ClockCircleOutlined />,
      };
    default:
      return {
        name: 'Không xác định',
        color: 'default',
        icon: <ClockCircleOutlined />,
      };
  }
};

export const genCensorshipStatus = (status: number, size = 12) => {
  switch (status) {
    case 0:
      return {
        name: 'Đang hoạt động',
        color: 'success',
        icon: (
          <CheckFilledIcon
            width={size}
            height={size}
            color={theme.colors.success}
          />
        ),
      };
    case 1:
      return {
        name: 'Dừng hoạt động',
        color: 'default',
        icon: <StopFilledIcon width={size} height={size} color="#59636E" />,
      };
    case 2:
      return {
        name: 'Chờ phê duyệt',
        color: 'warning',
        icon: (
          <WaitingFilledIcon
            width={size}
            height={size}
            color={theme.colors.warning}
          />
        ),
      };
    case 3:
      return {
        name: 'Bị hủy',
        color: 'error',
        icon: (
          <RejectFilledIcon
            width={size}
            height={size}
            color={theme.colors.error.main}
          />
        ),
      };
    default:
      return {
        name: 'Không xác định',
        color: 'default',
        icon: <ClockCircleOutlined style={{ fontSize: `${size}px` }} />,
      };
  }
};

export const genContactRequestStatus = (status: number, size = 12) => {
  switch (status) {
    case 0:
      return {
        name: 'Đã liên hệ',
        color: 'success',
        icon: (
          <CheckFilledIcon
            width={size}
            height={size}
            color={theme.colors.success}
          />
        ),
      };
    case 1:
      return {
        name: 'Chờ liên hệ',
        color: 'warning',
        icon: (
          <WaitingFilledIcon
            width={size}
            height={size}
            color={theme.colors.warning}
          />
        ),
      };
    case 2:
      return {
        name: 'Bị hủy',
        color: 'error',
        icon: (
          <RejectFilledIcon
            width={size}
            height={size}
            color={theme.colors.error.main}
          />
        ),
      };
    default:
      return {
        name: 'Không xác định',
        color: 'default',
        icon: <ClockCircleOutlined style={{ fontSize: `${size}px` }} />,
      };
  }
};

export const genStockAdjustmentStatus = (status: number, size = 12) => {
  switch (status) {
    case INVENTORY_AUDIT_STATUS.AUDITED:
      return {
        name: 'Đã cân bằng',
        color: 'success',
        icon: (
          <CheckFilledIcon
            width={size}
            height={size}
            color={theme.colors.success}
          />
        ),
      };
    case INVENTORY_AUDIT_STATUS.PENDING:
      return {
        name: 'Đang kiểm kho',
        color: 'warning',
        icon: (
          <WaitingFilledIcon
            width={size}
            height={size}
            color={theme.colors.warning}
          />
        ),
      };
    case INVENTORY_AUDIT_STATUS.CANCELED:
      return {
        name: 'Đã xóa',
        color: 'error',
        icon: (
          <RejectFilledIcon
            width={size}
            height={size}
            color={theme.colors.error.main}
          />
        ),
      };
    default:
      return {
        name: 'Không xác định',
        color: 'default',
        icon: <ClockCircleOutlined style={{ fontSize: `${size}px` }} />,
      };
  }
};

export const genBillStatus = (status: number, size = 12) => {
  switch (status) {
    case BILL_STATUS.ACTIVE:
      return {
        name: 'Đang hoạt động',
        color: 'success',
        icon: (
          <CheckFilledIcon
            width={size}
            height={size}
            color={theme.colors.success}
          />
        ),
        tooltip: "Hóa đơn đang hoạt động được hình thành sau khi đơn hàng hoàn thành"
      };
    case BILL_STATUS.DRAFT:
      return {
        name: 'Bản nháp',
        color: 'default',
        icon: (
          <StopFilledIcon width={size} height={size} color="#0E0C15" />
        ),
        tooltip: "Hóa đơn bản nháp được tạo ra từ đơn hàng chưa hoàn thành"
      };

    default:
      return {
        name: 'Không xác định',
        color: 'warning',
        icon: (
          <WaitingFilledIcon
            width={size}
            height={size}
            color={theme.colors.warning}
          />
        ),
        tooltip: "Hóa đơn không xác định"
      };
  }
}

export const genPaymentStatusBill = (status: number, size = 12) => {
  switch (status) {
    case 0:
      return {
        name: 'Ghi nợ',
        color: 'warning',
        icon: (
          <WaitingFilledIcon
            width={size}
            height={size}
            color={theme.colors.warning}
          />
        ),
      };
    case 1:
      return {
        name: 'Đã thanh toán',
        color: 'success',
        icon: (
          <CheckFilledIcon
            width={size}
            height={size}
            color={theme.colors.success}
          />
        ),
      };
    case 2:
      return {
        name: 'Đã huỷ',
        color: 'error',
        icon: (
          <RejectFilledIcon
            width={size}
            color={theme.colors.error.main}
          />
        ),
      };
    default:
      return {
        name: 'Ghi nợ',
        color: 'warning',
        icon: (
          <WaitingFilledIcon
            width={size}
            height={size}
            color={theme.colors.warning}
          />
        ),
      };
  }
};

export const genTypeBill = (type: number, size = 12) => {
  switch (type) {
    case BILL_TYPE.BILL:
      return {
        name: 'Mua hàng',
        color: theme.colors.primary.main,
        icon: (
          <WaitingFilledIcon
            width={size}
            height={size}
            color={theme.colors.warning}
          />
        ),
      };
    case BILL_TYPE.RETURN_BILL:
      return {
        name: 'Đổi trả',
        color: '#2db7f5',
        icon: (
          <CheckFilledIcon
            width={size}
            height={size}
            color={theme.colors.primary.main}
          />
        ),
      };
    default:
      return {
        name: 'Mua hàng',
        color: '#87d068',
        icon: (
          <WaitingFilledIcon
            width={size}
            height={size}
            color={theme.colors.warning}
          />
        ),
      };
  }
};

export const genStatusBill = (status: number, size = 12) => {
  switch (status) {
    case 0:
      return {
        name: 'Hóa đơn',
        color: theme.colors.primary.main,
        icon: (
          <WaitingFilledIcon
            width={size}
            height={size}
            color={theme.colors.warning}
          />
        ),
      };
    case 1:
      return {
        name: 'Hóa đơn tạm',
        color: '#52525A',
        icon: (
          <CheckFilledIcon
            width={size}
            height={size}
            color={theme.colors.primary.main}
          />
        ),
      };
    default:
      return {
        name: 'Hóa đơn',
        color: theme.colors.primary.main,
        icon: (
          <WaitingFilledIcon
            width={size}
            height={size}
            color={theme.colors.warning}
          />
        ),
      };
  }
};

export const genDigitalMenuStatus = (status: number, size = 12) => {
  switch (status) {
    case 0:
      return {
        name: 'Đang hoạt động',
        color: 'success',
        icon: (
          <CheckFilledIcon
            width={size}
            height={size}
            color={theme.colors.success}
          />
        ),
      };
    case 1:
      return {
        name: 'Dừng hoạt động',
        color: 'default',
        icon: <StopFilledIcon width={size} height={size} color="#59636E" />,
      };
    case 2:
      return {
        name: 'Chờ phê duyệt',
        color: 'warning',
        icon: (
          <WaitingFilledIcon
            width={size}
            height={size}
            color={theme.colors.warning}
          />
        ),
      };
    default:
      return {
        name: 'Không xác định',
        color: 'default',
        icon: <ClockCircleOutlined style={{ fontSize: `${size}px` }} />,
      };
  }
};

export const genAreaStatus = (status: number, createdAt?: any, size = 12) => {
  switch (status) {
    case 0:
      if (createdAt && createdAt && moment(createdAt).isAfter(moment(Date.now()))) {
        return {
          name: 'Chờ sản xuất',
          color: 'warning',
          icon: (
            <WaitingFilledIcon
              width={size}
              height={size}
              color={theme.colors.warning}
            />
          )
        }
      } else {
        return {
          name: 'Đang sản xuất',
          color: 'success',
          icon: (
            <CheckFilledIcon
              width={size}
              height={size}
              color={theme.colors.success}
            />
          ),
        };
      }
    case 1:
      return {
        name: 'Dừng sản xuất',
        color: 'default',
        icon: <StopFilledIcon width={size} height={size} color="#59636E" />,
      };
    case 2:
      return {
        name: 'Tạm dừng sản xuất',
        color: 'warning',
        icon: (
          <WaitingFilledIcon
            width={size}
            height={size}
            color={theme.colors.warning}
          />
        ),
      };
    default:
      return {
        name: 'Không xác định',
        color: 'default',
        icon: <ClockCircleOutlined style={{ fontSize: `${size}px` }} />,
      };
  }
};

export const genPartnerStatus = (status: number, size = 12) => {
  switch (status) {
    case 0:
      return {
        name: 'Đang liên kết',
        color: 'success',
        icon: (
          <CheckFilledIcon
            width={size}
            height={size}
            color={theme.colors.success}
          />
        ),
      };
    case 1:
      return {
        name: 'Dừng liên kết',
        color: 'default',
        icon: <StopFilledIcon width={size} height={size} color="#59636E" />,
      };
    case 2:
      return {
        name: 'Chờ phê duyệt',
        color: 'warning',
        icon: (
          <WaitingFilledIcon
            width={size}
            height={size}
            color={theme.colors.warning}
          />
        ),
      };
    case 4:
      return {
        name: 'Chờ xác nhận',
        color: 'warning',
        icon: (
          <WaitingFilledIcon
            width={size}
            height={size}
            color={theme.colors.warning}
          />
        ),
      };
    default:
      return {
        name: 'Không xác định',
        color: 'default',
        icon: <ClockCircleOutlined style={{ fontSize: `${size}px` }} />,
      };
  }
};

export const genEntityCategoryStatus = (status: number, size = 12) => {
  switch (status) {
    case 0:
      return {
        name: 'Đang hoạt động',
        color: 'success',
        icon: (
          <CheckFilledIcon
            width={size}
            height={size}
            color={theme.colors.success}
          />
        ),
      };
    case 1:
      return {
        name: 'Dừng hoạt động',
        color: 'default',
        icon: <StopFilledIcon width={size} height={size} color="#59636E" />,
      };
    case 2:
      return {
        name: 'Tạm dừng hoạt động',
        color: 'warning',
        icon: (
          <WaitingFilledIcon
            width={size}
            height={size}
            color={theme.colors.warning}
          />
        ),
      };
    case 3:
      return {
        name: 'Bản nháp',
        color: 'error',
        icon: (
          <RejectFilledIcon
            width={size}
            height={size}
            color={theme.colors.error.main}
          />
        ),
      };
    default:
      return {
        name: 'Không xác định',
        color: 'default',
        icon: <ClockCircleOutlined style={{ fontSize: `${size}px` }} />,
      };
  }
};

export const genComboType = (comboInfo: {
  type: number;
  buyQuantity: number;
  discount: number;
  value: number;
  specialPrice: number;
}) => {
  const { type, buyQuantity, discount, value, specialPrice } = comboInfo;
  switch (type) {
    case 0:
      return `Mua ${buyQuantity} giảm ${discount}%`;
    case 1:
      return `Mua ${buyQuantity} giảm ${formatMoney(value)}đ`;
    case 2:
      return `Mua ${buyQuantity} chỉ với giá ${formatMoney(specialPrice)}đ`;
    default:
      return '';
  }
};

export const genDiscountType = (discountInfo: {
  type: number;
  method: number;
}) => {
  const { type, method } = discountInfo;
  return `${DISCOUNT_TYPE_TEXT[type]} - ${DISCOUNT_METHOD_TEXT[method]}`;
};

export const genStockType = (stockInfo: {
  type: number;
}) => {
  const { type } = stockInfo;
  return `${STOCK_TYPE_TEXT[type]}`;
};

export const genDateTypeStatus = (status: number) => {
  switch (status) {
    case 0:
      return {
        name: 'Theo ngày',
        color: 'success',
      };
    case 1:
      return {
        name: 'Theo tuần',
        color: 'warning',
      };
    case 2:
      return {
        name: 'Theo tháng',
        color: 'blue',
      };
    case 3:
      return {
        name: 'Ngay khi có đơn hàng',
        color: 'purple',
      };
    default:
      return {
        name: 'Trống',
        color: 'default',
      };
  }
};

export const getOrderType = (type: number) => {
  switch (type) {
    case ORDER_TYPE.ALL:
      return { name: 'Tất cả loại đơn', color: 'purple' };
    case ORDER_TYPE.WHOLESALE:
      return { name: 'Đơn sỉ', color: 'cyan' };
    case ORDER_TYPE.RETAIL:
      return { name: 'Đơn lẻ', color: 'blue' };
    case ORDER_TYPE.CO_BUY:
      return { name: 'Đơn mua chung', color: 'magenta' };
    case ORDER_TYPE.DIGITAL_MENU:
      return { name: 'Đơn menu lẻ', color: 'green' };
    case ORDER_TYPE.WHOLESALE_DIGITAL_MENU:
      return { name: 'Đơn menu sỉ', color: 'red' };
    default:
      return { name: '', color: 'default' };
  }
};

export const getPartnerTypeInChain = (type: number, isInChain: boolean) => {
  if (type == PARTNER_TYPE.CUSTOMER) {
    if (isInChain) {
      return { name: 'ĐT nội bộ', color: 'orange' };
    }
    else {
      return { name: 'ĐT ngoài', color: 'blue' };
    }
  }

  if (type == PARTNER_TYPE.CENTRAL) {
    return { name: 'Trung tâm', color: 'red' };
  }

  if (type == PARTNER_TYPE.BRANCH) {
    return { name: 'CNCH', color: 'orange' };
  }

  if (type == PARTNER_TYPE.SUPPLIER) {
    if (isInChain) {
      return { name: 'NCC nội bộ', color: 'orange' };
    }
    else {
      return { name: 'NCC ngoài', color: 'blue' };
    }
  }

  if (type == PARTNER_TYPE.WAREHOUSE) {
    return { name: 'CNSX', color: 'orange' };
  }

  return { name: '', color: 'default' };

}

export const getEntityType = (type: number) => {
  switch (type) {
    case ENTITY_TYPE.FARM:
      return { name: 'Nông trại', color: 'green' };
    case ENTITY_TYPE.STORE:
      return { name: 'Cửa hàng', color: 'orange' };
    case ENTITY_TYPE.COLLABORATOR:
      return { name: 'Đại sứ bán hàng', color: 'blue' };
    default:
      return { name: '', color: 'default' };
  }
};


export const getEntityUrl = (type: number) => {
  switch (type) {
    case 1:
      return 'farm';
    case 2:
      return 'store';
    case 3:
      return 'collaborator';
    default:
      return '';
  }
};

export const activityType = (type: number) => {
  switch (type) {
    case 0:
      return 'đã mua đơn hàng';
    case 10:
      return 'gửi lời mời liên kết';
    case 11:
      return 'chấp nhận lời mời liên kết';
    case 20:
      return 'thực hiện chiến dịch';
    case 30:
      return 'cập nhật trạng thái đơn hàng';
    case 31:
      return 'gộp đơn hàng';
    case 32:
      return 'chuyển tiếp đơn hàng';
    case 33:
      return 'cập nhật thực giao và chuyển trạng thái giao hàng';
    case 34:
      return 'cập nhật thực nhận và chuyển trạng thái kiểm hàng';
    case 40:
      return 'yêu thích sản phẩm';
    case 41:
      return 'đánh giá sản phẩm';
    case 42:
      return 'đánh giá';
    case 43:
      return 'đánh giá đối tượng nuôi trồng';
    case 44:
      return 'mời bạn tham gia nhóm mua chung';
    case 45:
      return 'mời bạn tham gia chiến dịch';
    default:
      return 'thông báo';
  }
};

export const genNameListActivityType = (activityType: number) => {
  switch (activityType) {
    case 10: case 11:
      return 'linkedList';
    case 20:
      return 'systemList';
    case 30: case 31: case 32: case 33: case 34:
      return 'orderList';
    case 0:
      return 'requestList';
    default:
      return '';
  }
};

export const getCurrentLocation = (
  setIsLoading: (value: boolean) => void,
  onSetBodyUpdate?: ({
    address,
    provinceName,
    districtName,
    wardName,
  }: {
    address: string;
    provinceName: string | null;
    districtName: string | null;
    wardName: string | null;
  }) => void,
  device = DEVICES.DESKTOP
) => {
  setIsLoading(true);

  if (typeof window !== 'undefined' && window.navigator.geolocation) {
    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    };
    window.navigator.geolocation.getCurrentPosition(
      position => {
        const option = {
          latlng: `${position.coords.latitude},${position.coords.longitude}`,
          key: env.google.apiKey,
          language: 'vi',
        };

        try {
          GoogleApi.getCurrentLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          }).then((res: any) => {
            const { data } = res;

            data &&
              onSetBodyUpdate &&
              onSetBodyUpdate({
                address: data?.address,
                provinceName: data?.province?.fullName,
                districtName: data?.district?.fullName,
                wardName: data?.ward?.fullName,
              });
          });

        } catch (error: any) {
          Notification('error', 'Không lấy được vị trí', '', device);
        } finally {
          setIsLoading(false);
        }
      },
      () => {
        setIsLoading(false);
      },
      options
    );
  } else {
    setIsLoading(false);
    Notification('error', 'Thiết bị không hỗ trợ định vị', '', device);
  }
};

export const renderTextConfirm = (status: number) => {
  switch (status) {
    case ORDER_STATUS.PENDING:
      return 'Soạn đơn hàng';
    case ORDER_STATUS.PROCESSING:
      return 'Đóng gói đơn hàng';
    case ORDER_STATUS.DELIVERY:
      return 'Giao hàng';
    case ORDER_STATUS.CUSTOMER_CONFIRM:
      return 'Kiểm đơn hàng';
    case ORDER_STATUS.REJECT:
      return 'Hủy đơn hàng';
    case ORDER_STATUS.CUSTOMER_DRAFT:
      return 'Đưa vào đơn hàng nháp';
    case ORDER_STATUS.COMPLETE:
      return 'Hoàn thành đơn hàng';
    case ORDER_STATUS.PRE_PROCESSING:
      return 'Tiền xử lý đơn hàng';
    case ORDER_STATUS.SUPPLIER_CONFIRM:
      return 'Kiểm đơn hàng';
    case ORDER_STATUS.PENDING_EXECUTE:
      return 'Nhận đơn hàng';
    case ORDER_STATUS.SUPPLIER_DRAFT:
      return 'Đưa vào đơn hàng nháp';
    default:
      return `${status}`;
  }
};

export const filterOption = (input: string, option: any) => {
  if (
    removeVietnameseTones(
      typeof option.children === 'string'
        ? option.children.toLocaleLowerCase()
        : option.label.toLocaleLowerCase()
    ).includes(removeVietnameseTones(input.toLocaleLowerCase()))
  ) {
    return true;
  }
  return false;
};

export const getDummyArray = (size: number) =>
  Array(size)
    .fill('0')
    .map((item, index) => {
      return { key: index };
    });

export const genQRCodeNumber = (order: number) => {
  switch (true) {
    case order < 10:
      return `00${order}`;
    case order < 100:
      return `0${order}`;
    case order < 1000:
      return order;
    case order < 10000:
      return `00${order}`;
    case order < 100000:
      return `0${order}`;
    case order < 1000000:
      return order;
    default:
      return order;
  }
};

export const convertToSlug = (text: string) => {
  const a =
    'àáäâãåăæąçćčđďèéěėëêęğǵḧìíïîįłḿǹńňñòóöôœøṕŕřßşśšșťțùúüûǘůűūųẃẍÿýźžż·/_,:;';
  const b =
    'aaaaaaaaacccddeeeeeeegghiiiiilmnnnnooooooprrsssssttuuuuuuuuuwxyyzzz------';
  const p = new RegExp(a.split('').join('|'), 'g');
  const formatText = text
    .toString()
    .toLowerCase()
    .replace(/á|à|ả|ạ|ã|ă|ắ|ằ|ẳ|ẵ|ặ|â|ấ|ầ|ẩ|ẫ|ậ/gi, 'a')
    .replace(/é|è|ẻ|ẽ|ẹ|ê|ế|ề|ể|ễ|ệ/gi, 'e')
    .replace(/i|í|ì|ỉ|ĩ|ị/gi, 'i')
    .replace(/ó|ò|ỏ|õ|ọ|ô|ố|ồ|ổ|ỗ|ộ|ơ|ớ|ờ|ở|ỡ|ợ/gi, 'o')
    .replace(/ú|ù|ủ|ũ|ụ|ư|ứ|ừ|ử|ữ|ự/gi, 'u')
    .replace(/ý|ỳ|ỷ|ỹ|ỵ/gi, 'y')
    .replace(/đ/gi, 'd')
    .replace(/\s+/g, '-')
    .replace(p, c => b.charAt(a.indexOf(c)))
    .replace(/&/g, '-and-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');

  const uniqueNumber = moment().unix();
  return `${formatText}-${uniqueNumber}`;
};

export const getFullAddress = (
  address: string | undefined,
  district: string | undefined,
  province: string | undefined,
  ward?: string | undefined
) => {
  let fullAddress = '';
  if (address) {
    fullAddress += address.trim();
  }
  if (ward) {
    if (address) {
      fullAddress += `, `;
    }
    fullAddress += `${ward.trim()}`;
  }
  if (district) {
    fullAddress += `, ${district.trim()}`;
  }
  if (province) {
    fullAddress += `, ${province.trim()}`;
  }
  return fullAddress || 'Chưa cập nhật địa chỉ';
};

export const getPaymentMethod = (value: number) => {
  switch (value) {
    case ORDER_PAYMENT_METHOD.CASH:
      return 'Thanh toán khi nhận hàng';
    case ORDER_PAYMENT_METHOD.E_WALLET:
      return 'Ví điện tử';
    case ORDER_PAYMENT_METHOD.INTERNET_BANKING:
      return 'Internet Banking';
    case ORDER_PAYMENT_METHOD.CREDIT_CARD:
      return 'Thẻ tín dụng/Thẻ ghi nợ';
    case ORDER_PAYMENT_METHOD.DEBT:
      return 'Công nợ';
    default:
      return '';
  }
};

export const parseQueryString = (fieldKeys: any) => {
  if (fieldKeys['order.name'] || fieldKeys['order.value']) {
    fieldKeys.order = {
      name: fieldKeys['order.name'],
      value: Number(fieldKeys['order.value']),
    };

    delete fieldKeys['order.name'];
    delete fieldKeys['order.value'];
  }

  Object.keys(fieldKeys).forEach(key => {
    if (fieldKeys[key] === '') {
      delete fieldKeys[key];
    }
  });

  if (fieldKeys.page || fieldKeys.take) {
    fieldKeys.page = Number(fieldKeys.page);
    fieldKeys.take = Number(fieldKeys.take);
  }

  if (fieldKeys['where.status']) {
    if (!isNaN(Number(fieldKeys['where.status']))) {
      fieldKeys['where.status'] = Number(fieldKeys['where.status']);
    }
  }

  if (fieldKeys['where.product.status']) {
    fieldKeys['where.product.status'] = Number(
      fieldKeys['where.product.status']
    );
  }

  if (fieldKeys['where.objectType']) {
    fieldKeys['where.objectType'] = Number(fieldKeys['where.objectType']);
  }

  if (fieldKeys['where.totalStep']) {
    fieldKeys['where.totalStep'] = Number(fieldKeys['where.totalStep']);
  }
};

export const handleUploadImage = async (
  fileList: any[],
  action: (e: { file: any }) => void,
  device = DEVICES.DESKTOP
) => {
  if (!fileList || (Array.isArray(fileList) && fileList.length === 0)) {
    return undefined;
  }

  let result: any;

  if (fileList.length) {
    try {
      const fileData = fileList
        .filter(item => item.originFileObj)
        .map(item => item.originFileObj);

      let formatData = [];
      if (fileData.length) {
        const actionResult: any = await action({ file: fileData });
        const { data } = unwrapResult(actionResult);

        formatData = data.sort((a: any, b: any) => {
          if (a.image < b.image) return -1;
          if (a.image > b.image) return 1;
          return 0;
        }).map((item, index) => {
          return {
            isNew: true,
            type: 0,
            uid: `${Date.now()}${index}`,
            url: item.url[0],
          };
        });
      }

      result = fileList.filter(item => !item.originFileObj).concat(formatData);
    } catch (error: any) {
      Notification('error', 'Tải ảnh thất bại', error?.message, device);
    }
  } else {
    try {
      const actionResult: any = await action({ file: fileList });
      const { data } = unwrapResult(actionResult);
      result = data;
    } catch (error: any) {
      Notification('error', 'Tải ảnh thất bại', error?.message, device);
    }
  }

  return result;
};

export const handleUploadVideo = async (fileList: any[], action: any) => {
  if (!fileList || fileList === undefined) {
    return undefined;
  }
  let result: any;

  try {
    const actionResult: any = await action();
    const { data } = unwrapResult(actionResult);
    result = {
      ...data,
      isVideo: true,
    };
  } catch (error: any) {
    Notification('error', 'Tải video thất bại');
  }
  return result;
};

const unitPrefix = (unit: number) => {
  switch (unit) {
    case 0:
      return '';
    case 1:
      return 'bịch';
    case 2:
      return 'hộp';
    case 3:
      return 'vỉ';
    case 4:
      return 'phần';
    case 5:
      return 'cái';
    case 6:
      return 'thùng';
    case 7:
      return 'con';
    case 8:
      return 'hũ';
    case 9:
      return 'chai';
    case 10:
      return 'bó';
    case 11:
      return 'túi';
    case 12:
      return 'ổ';
    case 13:
      return 'set';
    case 14:
      return 'trái';
    case 15:
      return 'nải';
    case 16:
      return 'lọ';
    case 17:
      return 'cây';
    case 100:
      return 'khác';
    default:
      return '';
  }
};

const unitPostfix = (unit: number) => {
  switch (unit) {
    case 0:
      return 'gr';
    case 1:
      return 'kg';
    case 2:
      return 'yến';
    case 3:
      return 'tạ';
    case 4:
      return 'tấn';
    case 5:
      return 'cm';
    case 6:
      return 'm';
    case 7:
      return 'quả';
    case 8:
      return 'bịch';
    case 9:
      return 'ml';
    case 10:
      return 'l';
    case 11:
      return 'nải';
    case 12:
      return 'set';
    case 13:
      return 'gói';
    case 14:
      return 'cái';
    case 15:
      return 'túi';
    case 16:
      return 'con';
    case 17:
      return 'hộp';
    case 18:
      return 'chục';
    case 19:
      return 'chai';
    case 20:
      return 'thùng';
    case 21:
      return 'hũ';
    case 22:
      return 'khay';
    case 23:
      return 'cặp';
    case 24:
      return 'chiếc';
    case 25:
      return 'lọ';
    case 26:
      return 'đĩa';
    case 27:
      return 'ống';
    case 28:
      return 'niêu';
    case 29:
      return 'vỉ';
    case 30:
      return 'phần';
    case 31:
      return 'cành';
    case 32:
      return 'bó';
    case 33:
      return 'ổ';
    case 34:
      return 'cây';
    case 35:
      return 'bắp';
    case 100:
      return 'khác';
    default:
      return '';
  }
};

type UnitType = {
  unitPrefix: number;
  unitWeight: number;
  unitPostfix: number;
};

export const genUnit = (unit: UnitType): string => {
  const {
    unitPrefix: prefix,
    unitWeight: weight,
    unitPostfix: postfix,
  } = unit || {};
  const rs: string[] = [];

  if (prefix === 100) {
    return `đơn vị ${unitPrefix(prefix)}`;
  }

  rs.push(unitPrefix(prefix));

  if (weight != null) {
    rs.push(`${weight} ${unitPostfix(postfix)}`);
  }

  const productUnit = rs.join(' ');
  return productUnit.trim() || 'Chưa có đơn vị';
};

export const genWholesaleUnit = (
  unit: { unitPrefix: any; unitWeight: number; unitPostfix: any },
  isExport?: boolean
): string => {
  const rs: string[] = [];

  if (unit.unitPrefix?.name === 'khác' && !isExport) {
    return `đơn vị ${unit.unitPrefix?.name}`;
  }

  rs.push(unit.unitPrefix?.name);

  if (unit.unitWeight != null) {
    rs.push(`${unit.unitWeight} ${unit.unitPostfix?.name}`);
  }

  const productUnit = rs.join(' ');
  return productUnit.trim() || 'Chưa có đơn vị';
};

export const handleRedirect = (url: string) => {
  const win: any = window.open(`${url}`, '_blank');
  win.focus();
};

export const handleRedirectWithDomain = (
  domain: string | null | undefined,
  domainStatus: number | null | undefined,
  url: string
) => {
  if (domain && domainStatus === ACTIVE_STATUS.ACTIVATED) {
    handleRedirect(`${domain}/${url}`);
  } else {
    handleRedirect(`${env.service.hubhubWebApp}/${url}`);
  }
};

export const unflattenQuery = query => {
  const orderQuery: any = {};
  if (query.order) {
    orderQuery[query.order?.name] =
      query.order?.value === undefined ? -1 : query.order?.value;
  }

  const standardQuery = { ...unflatten(query), order: orderQuery };

  return standardQuery;
};

export function priceFormatter(num: number) {
  if (Math.abs(num) > 999999) {
    return `${Math.sign(num) * +(Math.abs(num) / 1000000).toFixed(1)}tr`;
  }

  return Math.abs(num) > 999
    ? `${Math.sign(num) * +(Math.abs(num) / 1000).toFixed(1)}k`
    : Math.sign(num) * Math.abs(num);
}

export const shouldUpdateReportQuantity = (currentQuantity, newQuantity) => {
  const {
    reportType: currentReportType,
    relativeQuantity: currentRelativeQuantity,
    absoluteQuantity: currentAbsoluteQuantity,
    isAutoProposed: currentIsAutoProposed,
  } = currentQuantity;
  const {
    reportType: newReportType,
    relativeQuantity: newRelativeQuantity,
    absoluteQuantity: newAbsoluteQuantity,
    isAutoProposed: newIsAutoProposed,
  } = newQuantity;
  if (newReportType != null && newReportType != currentReportType) {
    return true;
  }
  if (
    newRelativeQuantity != null &&
    newRelativeQuantity != currentRelativeQuantity
  ) {
    return true;
  }
  if (
    newAbsoluteQuantity != null &&
    newAbsoluteQuantity != currentAbsoluteQuantity
  ) {
    return true;
  }
  if (newIsAutoProposed != null && newIsAutoProposed != currentIsAutoProposed) {
    return true;
  }
  return false;
};

const isProductPriceUnitSameWithOldValue = (
  oldProductPriceUnit: ProductPriceUnit[],
  newProductPriceUnit: ProductPriceUnit[]
) => {
  if (oldProductPriceUnit.length !== newProductPriceUnit.length) {
    return false;
  }

  for (const oldPriceUnit of oldProductPriceUnit) {
    const newPriceUnit = newProductPriceUnit.find(
      item => item.id === oldPriceUnit.id
    );
    if (!newPriceUnit) {
      return false;
    }
    if (
      oldPriceUnit.price != newPriceUnit.price ||
      oldPriceUnit.prefix != newPriceUnit.prefix ||
      oldPriceUnit.weight != newPriceUnit.weight ||
      oldPriceUnit.postfix != newPriceUnit.postfix ||
      oldPriceUnit.isStandard != newPriceUnit.isStandard ||
      oldPriceUnit.isSelected != newPriceUnit.isSelected ||
      oldPriceUnit.isRemovable != newPriceUnit.isRemovable ||
      oldPriceUnit.isHidden != newPriceUnit.isHidden ||
      oldPriceUnit.displayedByDefault != newPriceUnit.displayedByDefault ||
      oldPriceUnit.allowDecimalQuantity != newPriceUnit.allowDecimalQuantity ||
      oldPriceUnit.isMin != newPriceUnit.isMin
    ) {
      return false;
    }
  }

  return true;
};

export const shouldUpdatePriceUnit = (
  currentValue: { priceUnit?: ProductPriceUnit[]; isPriceBlocked?: boolean },
  newValue: { priceUnit?: ProductPriceUnit[]; isPriceBlocked?: boolean }
) => {
  const { priceUnit: currentPriceUnit, isPriceBlocked: currentIsPriceBlocked } =
    currentValue;
  const { priceUnit: newPriceUnit, isPriceBlocked: newIsPriceBlocked } =
    newValue;

  if (
    currentPriceUnit != null &&
    newPriceUnit != null &&
    !isProductPriceUnitSameWithOldValue(currentPriceUnit, newPriceUnit)
  ) {
    return true;
  }
  if (newIsPriceBlocked != null && newIsPriceBlocked != currentIsPriceBlocked) {
    return true;
  }
  return false;
};

// 520px => 520
const getScreenWidth = (screen: string): number =>
  Number.parseFloat(screen.slice(0, -2));

export const getScreenWidthXS = (): number => getScreenWidth(theme.screen.xs);

export const getScreenWidthSM = (): number => getScreenWidth(theme.screen.sm);

export const getScreenWidthMD = (): number => getScreenWidth(theme.screen.md);

export const getScreenWidthLG = (): number => getScreenWidth(theme.screen.lg);

export const getCustomerReportQuantityAndPrice = ({
  supplierWholesaleUnit,
  customerWholesaleUnit,
  productUnitPostfixObj,
  supplierPrice,
  supplierOriginPrice,
  supplierQuantityObj,
}: {
  supplierWholesaleUnit: any;
  customerWholesaleUnit: any;
  productUnitPostfixObj: any;
  supplierOriginPrice?: number;
  supplierPrice?: number;
  supplierQuantityObj?: any;
}): {
  customerOriginPrice: number;
  customerPrice: number;
  customerQuantityObj: any;
} => {
  let customerOriginPrice = 0;
  let customerPrice = 0;
  let customerQuantityObj = {};

  const supplierProductUnitPostfix =
    productUnitPostfixObj[supplierWholesaleUnit?.postfix];
  const customerProductUnitPostfix =
    productUnitPostfixObj[customerWholesaleUnit?.postfix];

  if (!supplierProductUnitPostfix || !customerProductUnitPostfix) {
    return {
      customerOriginPrice,
      customerPrice,
      customerQuantityObj,
    };
  }

  const supplierDefaultQuantity =
    supplierWholesaleUnit.weight * supplierProductUnitPostfix.conversionValue;
  const customerDefaultQuantity =
    customerWholesaleUnit.weight * customerProductUnitPostfix.conversionValue;
  const supplierRatio =
    customerDefaultQuantity === 0
      ? 0
      : supplierDefaultQuantity / customerDefaultQuantity;

  if (supplierOriginPrice) {
    customerOriginPrice = Math.round(
      supplierOriginPrice / (supplierRatio || 1)
    );
  }

  if (supplierPrice) {
    customerPrice = Math.round(supplierPrice / (supplierRatio || 1));
  }

  if (supplierQuantityObj) {
    customerQuantityObj = Object.keys(supplierQuantityObj).reduce(
      (prevVal, curVal) => {
        prevVal[curVal] = roundProductQuantity(
          (supplierQuantityObj[curVal] || 0) * supplierRatio
        );
        return prevVal;
      },
      {}
    );
  }

  return {
    customerOriginPrice,
    customerPrice,
    customerQuantityObj,
  };
};

export const roundProductQuantity = (num: number, decimalPlaces = 6) =>
  Math.round((num + Number.EPSILON) * 10 ** decimalPlaces) /
  10 ** decimalPlaces;

export const getOrderPrevStatus = (currentStatus: number, trackingStatus: any[]) => {
  if (trackingStatus?.length) {
    const orderStatusIndex = trackingStatus.findIndex(item => item.orderStatus == currentStatus);
    return trackingStatus[orderStatusIndex - 1]?.orderStatus;
  }
  return undefined;
}

export const formatOrderItemsQuantitiesByOrderStatus = (
  orderItems: any[],
  orderStatus: number,
  trackingStatus: any[]
) => {
  const prevOrderStatus = getOrderPrevStatus(orderStatus, trackingStatus);
  const rs = orderItems.map(item => {
    let {
      supplierQuantity,
      supplierDeliveredQuantity,
      customerReceivedQuantity,
    } = item;

    const hideQuantityByOrderStatus = (status: number) => {
      switch (status) {
        case -2: // tao don hang
        case -3: // tao don hang nhap
        case -4: // tao don bo sung
        case -5: // tao don gop
        case -6: // tao don chuyen tiep
        case ORDER_STATUS.CUSTOMER_DRAFT:
        case ORDER_STATUS.SUPPLIER_DRAFT:
        case ORDER_STATUS.PRE_PROCESSING:
        case ORDER_STATUS.PENDING_EXECUTE:
        case ORDER_STATUS.PENDING: {
          supplierQuantity = null;
          supplierDeliveredQuantity = null;
          customerReceivedQuantity = null;
          break;
        }
        case ORDER_STATUS.PROCESSING: {
          supplierDeliveredQuantity = null;
          customerReceivedQuantity = null;
          break;
        }
        case ORDER_STATUS.DELIVERY: {
          customerReceivedQuantity = null;
          break;
        }
        default:
          break;
      }
    }

    hideQuantityByOrderStatus(orderStatus);

    if ([ORDER_STATUS.REJECT, ORDER_STATUS.MERGED].includes(orderStatus) && prevOrderStatus != undefined) {
      hideQuantityByOrderStatus(prevOrderStatus);
    }

    return {
      ...item,
      supplierQuantity,
      supplierDeliveredQuantity,
      customerReceivedQuantity,
    };
  });

  return rs;
};

export const getOrderItemQuantityByStatus = ({
  orderItem,
  status,
  trackingStatus,
}: {
  orderItem: any;
  status: number;
  trackingStatus: any[];
}) => {
  const prevStatus = getOrderPrevStatus(status, trackingStatus);
  let quantity = orderItem.customerQuantity || 0;

  const getQuantityByOrderStatus = (orderStatus: number) => {
    switch (orderStatus) {
      case ORDER_STATUS.PROCESSING:
        quantity = orderItem.supplierQuantity;
        break;

      case ORDER_STATUS.DELIVERY:
        quantity = orderItem.supplierDeliveredQuantity;
        break;

      case ORDER_STATUS.CUSTOMER_CONFIRM:
      case ORDER_STATUS.SUPPLIER_CONFIRM:
      case ORDER_STATUS.COMPLETE:
        quantity = orderItem.customerReceivedQuantity;
        break;
      default:
        break;
    }
  };

  getQuantityByOrderStatus(status);

  if ([ORDER_STATUS.REJECT, ORDER_STATUS.MERGED].includes(status) && prevStatus != undefined) {
    getQuantityByOrderStatus(prevStatus);
  }

  return quantity;
};

export const getOrderItemTotalPrice = (orderItem: any, status: number, trackingStatus: any[]) => {
  const quantity = getOrderItemQuantityByStatus({ orderItem, status, trackingStatus });
  return formatMoney(Math.round(quantity * orderItem.originPrice - orderItem.totalAdjustment));
};

export const validateInputNumberOnBlur = (
  value: number,
  min: number,
  max?: number
) => {
  let validatedValue = value;
  if (validatedValue < min) {
    validatedValue = min;
  }
  if (max != null && validatedValue > max) {
    validatedValue = max;
  }
  return validatedValue;
};

export const displayTotalSupplierProposedQuantity = (
  totalSupplierProposedQuantity: any
): string => {
  if (totalSupplierProposedQuantity.relative === 0) {
    return formatQuantity(totalSupplierProposedQuantity.absolute).toString();
  }
  return reportQuantity({
    reportType: 0,
    relativeQuantity: totalSupplierProposedQuantity.relative,
    absoluteQuantity: 0,
  });
};

export const getOrderTypeVer3 = (orderType: string): string => {
  switch (orderType) {
    case 'retail':
      return 'Đơn lẻ';
    case 'wholesale':
      return 'Đơn sỉ';
    case 'coBuy':
      return 'Đơn mua chung';
    case 'digitalMenu':
      return 'Đơn menu lẻ';
    case 'wholesaleDigitalMenu':
      return 'Đơn menu sỉ';
    default:
      return 'Đơn trống';
  }
};

export const isWholesaleOrder = (orderType: number) =>
  [ORDER_TYPE.WHOLESALE, ORDER_TYPE.WHOLESALE_DIGITAL_MENU].includes(orderType);

export const calcOrderTemporaryPrice = ({
  totalPrice,
  totalAdjustment,
  deliveryFee,
}: {
  totalPrice: number;
  totalAdjustment: number;
  deliveryFee: number;
}) =>
  Number(totalPrice || 0) +
  Number(totalAdjustment || 0) -
  Number(deliveryFee || 0);

export const sharePhoneNumber = (phoneNumber: string) => {
  copyLink(phoneNumber, 'số điện thoại');
};

export const copyLink = (link: string | undefined, content?: string) => {
  if (link) {
    navigator.clipboard.writeText(link);
    Notification(
      'success',
      `Sao chép ${content ? `${content} ` : ''}thành công`,
      ''
    );
  } else {
    Notification(
      'error',
      `Sao chép ${content ? `${content} ` : ''}thất bại`,
      ''
    );
  }
};

export const copyText = (content: string | undefined) => {
  if (content) {
    navigator.clipboard.writeText(content);
    Notification('success', `Sao chép thành công`, '');
  } else {
    Notification('error', `Sao chép thất bại`, '');
  }
};

export const buildEntityDomain = ({
  entityType,
  entitySlug,
  domain,
  domainStatus,
  website,
}: {
  entityType: number;
  entitySlug: string;
  domainStatus: number;
  domain: string;
  website: string;
}) => {
  if (domain && domainStatus === DOMAIN_STATUS.ACTIVATE) {
    return domain;
  }
  if (website) {
    return website;
  }
  const entityUrl = getEntityUrl(entityType);
  if (entityUrl) {
    if (entityType === ENTITY_TYPE.FARM) {
      return `${env.service.hubsupplyWebApp}/${entityUrl}/${entitySlug}`;
    }
    return `${env.service.hubhubWebApp}/${entityUrl}/${entitySlug}`;
  }
  return '';
};

export const formatDate = (date: string | moment.MomentInput | undefined, format = 'DD/MM/YYYY') => {
  if (date) {
    return moment(date).format(format);
  }
  return 'Chưa cập nhật';
};

export const getDateRange = (start, end, timeGroupType): any => {
  const startDate = moment(start).toISOString();
  const endDate = moment(end).toISOString();

  if (
    moment(startDate).endOf(timeGroupType).format("DD/MM/YYYY") === moment(endDate).endOf(timeGroupType).format("DD/MM/YYYY")
  ) {
    return [moment(startDate).startOf('day'), moment(endDate).endOf('day')];
  }

  let startTimeGroupType = moment(startDate).endOf(timeGroupType);
  let endTimeGroupType = moment(endDate).startOf(timeGroupType);

  if (
    moment(startTimeGroupType).format('DD/MM/YYYY') ===
    moment(startDate).format('DD/MM/YYYY')
  ) {
    startTimeGroupType = moment(startTimeGroupType)
      .clone()
      .add(1, timeGroupType)
      .endOf(timeGroupType);
  }

  if (
    moment(endTimeGroupType).format('DD/MM/YYYY') ===
    moment(startDate).format('DD/MM/YYYY')
  ) {
    endTimeGroupType = moment(endTimeGroupType)
      .clone()
      .subtract(1, timeGroupType)
      .endOf(timeGroupType);
  }

  const dateArray: any[] = [];
  while (startTimeGroupType <= endTimeGroupType) {
    dateArray.push(startTimeGroupType);
    startTimeGroupType = moment(startTimeGroupType)
      .clone()
      .add(1, timeGroupType)
      .endOf(timeGroupType);
  }

  return [
    moment(startDate).startOf('day'),
    ...dateArray,
    moment(endDate).endOf('day'),
  ];
};

export const formatDateToShow = ({
  timeGroupType,
  date,
  startDate,
  endDate,
}: {
  timeGroupType: any,
  date: any,
  startDate?: any,
  endDate?: any,
}) => {
  let startDateString = startDate ? moment(startDate).format('DD/MM') : undefined
  let endDateString = endDate ? moment(endDate).format('DD/MM') : undefined

  switch (timeGroupType) {
    case TIME_GROUP_TYPE.DAY:
      return {
        day: VN_DAY[moment(date).format('dddd')],
        date: moment(date).format('DD/MM'),
      };
    case TIME_GROUP_TYPE.WEEK: {
      startDateString = startDateString || moment(date).startOf('week').format('DD/MM');
      endDateString = endDateString || moment(date)
        .endOf('week')
        .format('DD/MM');

      return {
        day: `Tuần ${moment(date).format('W')}`,
        date: `${startDateString} - ${endDateString}`,
      };
    }
    case TIME_GROUP_TYPE.MONTH:
      startDateString = startDateString || moment(date).startOf('month').format('DD/MM');
      endDateString = endDateString || moment(date)
        .endOf('month')
        .format('DD/MM');

      return {
        day: `Tháng ${moment(date).format('M')}`,
        date: `${startDateString} - ${endDateString}`,
      };
    case TIME_GROUP_TYPE.QUARTER:
      startDateString = startDateString || moment(date).startOf('quarter').format('DD/MM');
      endDateString = endDateString || moment(
        date
      )
        .endOf('quarter')
        .format('DD/MM');

      return {
        day: `Quý ${moment(date).format('Q')}`,
        date: `${startDateString} - ${endDateString}`,
      };
    case TIME_GROUP_TYPE.YEAR:
      startDateString = startDateString || moment(date).startOf('year').format('DD/MM');
      endDateString = endDateString || moment(
        date
      )
        .endOf('year')
        .format('DD/MM');

      return {
        day: `Năm ${moment(date).format('Y')}`,
        date: `${startDateString} - ${endDateString}`,
      };

    default:
      return {
        day: '',
        date: '',
      };
  }
};

export const getTimeListData = (start, end, timeGroupType): any => {
  const startDate = moment(start).toISOString();
  const endDate = moment(end).toISOString();
  let dateList: any = [];
  const result = getDateRange(startDate, endDate, timeGroupType);

  if (timeGroupType === TIME_GROUP_TYPE.DAY) {
    dateList = result.map(item => {
      return {
        id: moment(item).format('DD/MM/YYYY'),
        key: moment(item).format('DD/MM/YYYY'),
        day: formatDateToShow({
          timeGroupType,
          date: item,
        }).day,
        date: formatDateToShow({
          timeGroupType,
          date: item,
        }).date,
        entityProducts: {},
      };
    });
  } else {
    if (result.length > 2) {
      const [start, ...list] = result;

      dateList = list.map((item, index) => {
        return {
          id: moment(item).format('DD/MM/YYYY'),
          key: moment(item).format('DD/MM/YYYY'),
          day: formatDateToShow({
            timeGroupType,
            date: item,
          }).day,
          date: formatDateToShow({
            timeGroupType,
            date: item,
            startDate: index == 0 ? start : undefined,
            endDate: index == list.length - 1 ? endDate : undefined,
          }).date,
          entityProducts: {},
        };
      });
    } else if (result.length == 2) {
      dateList = [
        {
          id: moment(result[1]).format('DD/MM/YYYY'),
          key: moment(result[1]).format('DD/MM/YYYY'),
          day: formatDateToShow({
            timeGroupType,
            date: result[1],
          }).day,
          date: formatDateToShow({
            timeGroupType,
            date: result[1],
            startDate: result[0],
            endDate: result[1],
          }).date,
          entityProducts: {},
        },
      ];
    }
  }

  return dateList;
};

export const checkCanUpdateOrderNote = (
  order: any,
  currentEntity: any,
  noteType: 'ORDER' | 'ORDER_ITEM'
) => {
  const { type: entityType, id: entityID, isCentral } = currentEntity;
  const { status, customerID, collaboratorID, supplierID } = order;
  if (noteType === 'ORDER') {
    if (entityID === customerID || entityID === collaboratorID) {
      return true;
    }
    if (
      ((entityType === ENTITY_TYPE.FARM || entityType === ENTITY_TYPE.STORE) &&
        entityID === supplierID) ||
      isCentral
    ) {
      return true;
    }
    return false;
  }
  if (noteType === 'ORDER_ITEM') {
    if (entityID === customerID || entityID === collaboratorID) {
      return true;
    }
    if (
      ((entityType === ENTITY_TYPE.FARM || entityType === ENTITY_TYPE.STORE) &&
        entityID === supplierID) ||
      isCentral
    ) {
      return true;
    }
    return false;
  }
  return false;
};

export const genOrderStatus = (status: number, customerOrderText?: any) => {

  const ORDER_STATUS_TEXT_VALUE = customerOrderText || ORDER_STATUS_TEXT;

  switch (status) {
    case ORDER_STATUS.ERROR:
      return {
        name: ORDER_STATUS_TEXT_VALUE[ORDER_STATUS.ERROR],
        color: '#eb403d',
        colorHex: '#eb403d',
      };
    case ORDER_STATUS.ALL:
      return {
        name: ORDER_STATUS_TEXT_VALUE[ORDER_STATUS.ALL],
        color: '#C4C4C4',
        colorHex: '#C4C4C4',
      };
    case ORDER_STATUS.PENDING:
      return {
        name: ORDER_STATUS_TEXT_VALUE[ORDER_STATUS.PENDING],
        color: '#c41d7f',
        colorHex: '#c41d7f',
      };
    case ORDER_STATUS.PROCESSING:
      return {
        name: ORDER_STATUS_TEXT_VALUE[ORDER_STATUS.PROCESSING],
        color: '#faad14',
        colorHex: '#faad14',
      };
    case ORDER_STATUS.DELIVERY:
      return {
        name: ORDER_STATUS_TEXT_VALUE[ORDER_STATUS.DELIVERY],
        color: '#1890ff',
        colorHex: '#1890ff',
      };
    case ORDER_STATUS.CUSTOMER_CONFIRM:
      return {
        name: ORDER_STATUS_TEXT_VALUE[ORDER_STATUS.CUSTOMER_CONFIRM],
        color: '#531dab',
        colorHex: '#531dab',
      };
    case ORDER_STATUS.REJECT:
      return {
        name: ORDER_STATUS_TEXT_VALUE[ORDER_STATUS.REJECT],
        color: '#f5222d',
        colorHex: '#f5222d',
      };
    case ORDER_STATUS.CUSTOMER_DRAFT:
      return {
        name: ORDER_STATUS_TEXT_VALUE[ORDER_STATUS.CUSTOMER_DRAFT],
        color: '#C4C4C4',
        colorHex: '#C4C4C4',
      };
    case ORDER_STATUS.COMPLETE:
      return {
        name: ORDER_STATUS_TEXT_VALUE[ORDER_STATUS.COMPLETE],
        color: '#4eb92d',
        colorHex: '#4eb92d',
      };
    case ORDER_STATUS.PRE_PROCESSING:
      return {
        name: ORDER_STATUS_TEXT_VALUE[ORDER_STATUS.PRE_PROCESSING],
        color: '#d4380d',
        colorHex: '#d4380d',
      };
    case ORDER_STATUS.SUPPLIER_CONFIRM:
      return {
        name: ORDER_STATUS_TEXT_VALUE[ORDER_STATUS.SUPPLIER_CONFIRM],
        color: '#1d39c4',
        colorHex: '#1d39c4',
      };
    case ORDER_STATUS.PENDING_EXECUTE:
      return {
        name: ORDER_STATUS_TEXT_VALUE[ORDER_STATUS.PENDING_EXECUTE],
        color: '#08979c',
        colorHex: '#08979c',
      };
    case ORDER_STATUS.SUPPLIER_DRAFT:
      return {
        name: ORDER_STATUS_TEXT_VALUE[ORDER_STATUS.SUPPLIER_DRAFT],
        color: '#7cb305',
        colorHex: '#7cb305',
      };
    case ORDER_STATUS.ADDITIONAL_ORDER:
      return {
        name: ORDER_STATUS_TEXT_VALUE[ORDER_STATUS.ADDITIONAL_ORDER],
        color: '#4eb92d',
        colorHex: '#4eb92d',
      };
    case ORDER_STATUS.MERGED:
      return {
        name: ORDER_STATUS_TEXT_VALUE[ORDER_STATUS.MERGED],
        color: '#4eb92d',
        // icon: <SnippetsFilled className="order-status-icon" style={{ fontSize: 16 }} />,
        colorHex: '#4eb92d',
      };
    default:
      return {
        name: 'Trống',
        color: '#C4C4C4',
        // icon: <ClockCircleOutlined className="order-status-icon" />,
        colorHex: '#C4C4C4',
      };
  }
};

export const genProductionPlanStatus = (status: number, time: { startedAt: string, finishedAt: string }) => {
  const { startedAt, finishedAt } = time;
  switch (status) {
    case PRODUCTION_PLAN_STATUS.ALL:
      return {
        name: "Tất cả",
        color: '#C4C4C4',
        colorHex: '#C4C4C4',
      };
    case PRODUCTION_PLAN_STATUS.ACTIVE:
      if (startedAt && moment(startedAt).isAfter(moment(Date.now()))) {
        return {
          name: "Chờ sản xuất",
          color: '#c41d7f',
          colorHex: '#c41d7f',
        };
      }

      if (finishedAt && moment(finishedAt).isBefore(moment(Date.now()))) {
        return {
          name: "Hoàn thành",
          color: '#faad14',
          colorHex: '#faad14',
        };
      }
      return {
        name: "Đang sản xuất",
        color: '#4eb92d',
        colorHex: '#4eb92d',
      };
    case PRODUCTION_PLAN_STATUS.DEACTIVE:
      return {
        name: "Đã đóng",
        color: '#C4C4C4',
        colorHex: '#C4C4C4',
      };
    default:
      return {
        name: 'Trống',
        color: '#C4C4C4',
        colorHex: '#C4C4C4',
      };
  }
};

export const genStampType = (type: number) => {
  switch (type) {
    case 4:
      return {
        name: 'Đối tượng',
        color: '#08979c',
        colorHex: '#08979c',
      };
    case 1:
    case 3:
      return {
        name: 'Sản phẩm',
        color: '#4eb92d',
        colorHex: '#4eb92d',
      };
    case 5:
      return {
        name: 'Vùng sản xuất',
        color: '#1d39c4',
        colorHex: '#1d39c4',
      };
    default:
      return {
        name: 'Tem trống',
        color: '#52525A',
        colorHex: '#52525A',
      };
  }
};


export const exportImage = async ({
  imageName,
  imageType,
  ref,
  isMulti,
}: {
  imageName: string;
  imageType: string;
  ref: any;
  isMulti?: boolean;
}) => {
  const nameExport = imageName || `QR`;
  let element;

  if (isMulti) {
    element = ref;
  } else {
    element = ref.current;
  }

  const canvas = await html2canvas(element, {
    allowTaint: true,
    useCORS: true,
    scale: 2,
  });
  const data = canvas.toDataURL(`${nameExport}/${imageType}`);
  const link = document.createElement('a');

  if (typeof link.download !== 'string' && isMulti) {
    window.open(data);
  } else {
    link.href = data;
    link.download = buildExportFileName(nameExport);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export const isEmptyString = (str: string) => {
  if (!str) {
    return true;
  }
  if (typeof str === 'string' && str.replace(/\s/g, '').length === 0) {
    return true;
  }
  return false;
};

export const isEmptyObject = (obj: any) => {
  if (obj === null) {
    return true;
  }
  return Object.keys(obj).length === 0;
};

export const removeSpaceString = (str: string) => str.replace(/\s/g, '');

export const removeDuplicateLineBreakString = (str: string) =>
  str.replace(/(\r\n|\r|\n){2}/g, '\n');

export const removeDuplicateSpaceString = (str: string) =>
  str.replace(/\s\s+/g, ' ').trim();

export const removeDuplicateSpaceString2 = (str: string) =>
  str.split(/\s+/).join(' ');

export const removeLineBreakString = (str: string) =>
  str.replace(/(\r\n|\r|\n){2}/g, '$1').replace(/(\r\n|\r|\n){3,}/g, '$1\n');

export const formatNoteText = (str?: string) => {
  if (str) {
    return removeDuplicateLineBreakString(removeDuplicateSpaceString(str));
  }
  return '';
};

export const getPaymentMethodWithEntityType = (type: number) => {
  if (type == ENTITY_TYPE.COLLABORATOR) {
    return ORDER_PAYMENT_METHOD.INTERNET_BANKING;
  }

  if (type == ENTITY_TYPE.STORE || type == ENTITY_TYPE.FARM) {
    return ORDER_PAYMENT_METHOD.DEBT;
  }

  return DEFAULT_ORDER_PAYMENT_METHOD;
};

export const formatMoneyBigint = (number: bigint, separator = ",") => {
  if (number == null) return '0đ';
  // return number.toLocaleString('de-DE', { style: 'currency', currency: 'VND' });
  let str = `${number.toLocaleString('vi', { currency: 'VND' })}đ`;
  str = str.replaceAll(/\.|\,/g, separator).replace(/-/g, '- ');
  return str;
};

export const buildProductWholesaleUnit = (unit: {
  unitPrefix: any;
  unitWeight: number;
  unitPostfix: any;
}): string => {
  const rs: string[] = [];

  if (unit.unitPrefix?.name === 'khác') {
    return `đơn vị ${unit.unitPrefix?.name}`;
  }

  rs.push(unit.unitPrefix?.name);

  if (unit.unitWeight != null) {
    rs.push(`${unit.unitWeight} ${unit.unitPostfix?.name}`);
  }

  const productUnit = rs.join(' ');
  return productUnit.trim() || 'Chưa có đơn vị';
};

export const formatQueryMultiple = orderQuery => {
  const tempStatus = orderQuery['where.status'];
  const tempType = orderQuery['where.type'];

  if (tempStatus?.length && Array.isArray(tempStatus)) {
    orderQuery['where.status'] = {
      in: tempStatus.filter(order => typeof order === 'number'),
    };
  }

  if (tempType?.length && Array.isArray(tempType)) {
    orderQuery['where.type'] = {
      in: tempType.filter(order => typeof order === 'number'),
    };
  }

  return orderQuery;
};

export const sortProductByUpdatedAt = (data: any[]) =>
  data.sort((a, b) => {
    if (a?.updatedAt && b?.updatedAt) {
      const dateA = Date.parse(a?.updatedAt);
      const dateB = Date.parse(b?.updatedAt);

      if (dateA < dateB) {
        return -1;
      }

      if (dateA > dateB) {
        return 1;
      }

      if (dateA == dateB) {
        return 0;
      }
    }

    if (a?.updatedAt && !b?.updatedAt) {
      return -1;
    }

    return 0;
  });

export const canOrderBeUpdatedBySupplier = (orderStatus: number) =>
  [
    ORDER_STATUS.PRE_PROCESSING,
    ORDER_STATUS.PENDING_EXECUTE,
    ORDER_STATUS.PENDING,
    ORDER_STATUS.PROCESSING,
    ORDER_STATUS.SUPPLIER_DRAFT,
    ORDER_STATUS.CUSTOMER_DRAFT,
    // ORDER_STATUS.DELIVERY,
  ].includes(orderStatus);

export const displayOrderItemQuantity = (
  orderItem: any,
  quantityKey: string
) => {
  if (orderItem[quantityKey] === null) {
    return '...';
  }
  return orderItem[quantityKey] || 0;
};

export const convertAdditionalInfoAddressToAddressData = (
  address: any
): AddressData | undefined => {
  if (!address) {
    return undefined;
  }
  return {
    ...address,
    ward: {
      ...address.ward,
      province: { ...address.province },
      district: { ...address.district },
    },
  };
};

export const compareAddress = (
  address1: AddressData | undefined,
  address2: AddressData | undefined
) => {
  if (!address1 || !address2) {
    return false;
  }
  if (
    address1?.ward?.id !== address2?.ward?.id ||
    address1?.title != address2?.title ||
    address1?.phoneNumber !== address2?.phoneNumber ||
    address1?.address !== address2?.address
    // address1?.isHome !== address2?.isHome
  ) {
    return false;
  }
  return true;
};

export const checkAndLoadLastPage = (
  data: any[],
  pagination: any,
  fetchData: (e) => void
) => {
  if (pagination.total !== 0) {
    const lastPaginationPage = Math.ceil(pagination.total / pagination.take);

    if (
      pagination?.page !== 1 &&
      data?.length === 0 &&
      pagination?.current > lastPaginationPage
    ) {
      fetchData(lastPaginationPage);
      return true;
    }
    return false;
  }
  return false;
};

export const getListQueryString = (
  queryStrings: {
    field: string;
    defaultValue: string | undefined;
    whiteList?: string[];
  }[]
): any[] => {
  const { search } = useLocation();
  return queryStrings.map(query =>
    getQueryString({
      search,
      field: query.field,
      defaultValue: query.defaultValue,
      whiteList: query?.whiteList,
    })
  );
};

export const formatFullTextSearchValue = (name: string, removeDoubleSpace = true): string => {
  if (name?.trim()) {
    if (isVietnamesePhoneNumber(name.trim())) {
      return formatPhoneNumber(name);
    }
    return removeDoubleSpace ? name?.replace(/\s+/g, ' ').trim() : name.trim();
  }
  return '';
};

export const genUserRole = (role: string) => {
  switch (role) {
    case ORDER_ENTITY_ROLE.SUPPLIER_CENTRAL:
      return 'Trung tâm của NCC';
    case ORDER_ENTITY_ROLE.CUSTOMER_CENTRAL:
      return 'Trung tâm của KH';
    case ORDER_ENTITY_ROLE.SUPPLIER_CENTRAL_STAFF:
      return 'Nhân viên trung tâm của NCC';
    case ORDER_ENTITY_ROLE.SUPPLIER_CENTRAL_MANAGER:
      return 'Quản lý trung tâm của NCC';
    case ORDER_ENTITY_ROLE.CUSTOMER_CENTRAL_STAFF:
      return 'Nhân viên trung tâm của KH';
    case ORDER_ENTITY_ROLE.CUSTOMER_CENTRAL_MANAGER:
      return 'Quản lý trung tâm của KH';
    case ORDER_ENTITY_ROLE.OWNER:
      return 'Chủ sở hữu';
    case ORDER_ENTITY_ROLE.CUSTOMER:
      return 'Khách hàng';
    case ORDER_ENTITY_ROLE.SUPPLIER:
      return 'Nhà cung cấp';
    case ORDER_ENTITY_ROLE.COLLABORATOR:
      return 'Cộng tác viên';
    case ORDER_ENTITY_ROLE.SUPPLIER_STAFF:
      return 'Nhân viên của NCC';
    case ORDER_ENTITY_ROLE.SUPPLIER_MANAGER:
      return 'Quản lý của NCC';
    case ORDER_ENTITY_ROLE.CUSTOMER_STAFF:
      return 'Nhân viên của KH';
    case ORDER_ENTITY_ROLE.CUSTOMER_MANAGER:
      return 'Quản lý của KH';
    case ORDER_ENTITY_ROLE.ORDER_SUPPLIER_ASSIGNEE:
      return 'Nhân viên phân công của NCC';
    case ORDER_ENTITY_ROLE.ORDER_CUSTOMER_ASSIGNEE:
      return 'Nhân viên phân công của KH';
    case ORDER_ENTITY_ROLE.SYSTEM_ADMIN:
      return 'Hệ thống';
    // old role version
    case ORDER_ENTITY_ROLE.MANAGER:
      return 'Quản lý';
    case ORDER_ENTITY_ROLE.EMPLOYEE:
      return 'Nhân viên';
    default:
      return '';
  }
};

export const buildTableSkeletonData = (
  columns: Array<{
    title: string;
    type?: 'image' | 'button' | 'banner' | 'input';
    width?: number | string;
  }>
) =>
  columns.map(item => {
    let width: any = 80;
    if (item.width && typeof item.width === 'string') {
      width = Number(item.width.substring(0, item.width.length - 1)) * 7;
    } else if (item.width && typeof item.width === 'number') {
      width = Number(item.width);
    }
    if (typeof item.width === 'number' && item.width > 120) {
      width = 120;
    }
    return {
      title: item.title,
      type: item.type || 'input',
      width,
    };
  });

export const isOnlyNumberString = (str: string) => /^\d+$/.test(str);

export const stringReplaceAll = (
  string: string,
  search: string,
  replace: string
) => string.split(search).join(replace);

export const isVideoFile = (file: string) => {
  const videoExtensions = ['mp4', 'avi', 'mov', 'mkv', 'webm', 'flv', 'wmv', 'mpeg'];

  const fileSplit = file.split('.');

  // Lấy phần mở rộng của file (không phân biệt hoa/thường)
  const fileExtension = fileSplit[fileSplit.length - 1].toLowerCase();

  // Kiểm tra phần mở rộng có nằm trong danh sách video không
  return videoExtensions.includes(fileExtension);
};

export const checkStringIncludeAnotherCharacter = (
  str: string,
  validStr: string[]
) => {
  for (const charItem of validStr) {
    str = stringReplaceAll(str, charItem, '');
  }
  if (str.length > 0) {
    return false;
  }
  return true;
};

export const formatSearchText = str => {
  const searchText = str.trim();
  let formatSearchText = removeDuplicateSpaceString(searchText);
  // Check string length not empty
  if (removeDuplicateSpaceString(searchText).length > 0) {
    // format if vietnam phone
    if (isVietnamesePhoneNumber(formatSearchText)) {
      formatSearchText = formatPhoneNumber(searchText);
    }
    // check search phone 0906 1234 need to remove space
    else if (
      checkStringIncludeAnotherCharacter(formatSearchText, CELLPHONE_CHARS)
    ) {
      if (formatSearchText[0] === '0') {
        formatSearchText = removeSpaceString(formatSearchText).substring(1);
      } else {
        formatSearchText = removeSpaceString(formatSearchText);
      }
    }
    // check string is numberic
    else if (isOnlyNumberString(searchText)) {
      if (formatSearchText[0] === '0') {
        formatSearchText.substring(1);
      }
    }

    return formatSearchText;
  }
  return '';
};

export const formatOrderQuery = query =>
  query?.order?.name
    ? { [query?.order?.name]: query?.order?.value }
    : { createdAt: 0 };

export const formatFormOrderQuery = query =>
  query.order?.name ? query?.order : { name: 'createdAt', value: 0 };

export const getSpreadSheetIDFromGGSheetLink = (link: string): string => {
  const ggSheetID = link?.match(/\/d\/(.+)\//);
  if (ggSheetID) {
    return ggSheetID[1];
  }
  return '';
};

export const formatImageUrl = (
  url?: string,
  type?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg',
  convertSVGToImage?: boolean
) => {
  if (convertSVGToImage) {
    if (url?.includes("ui-avatars")) {
      return `${url}&format=png`;
    }
  }
  return url || '';
};

// export const formatImageUrl = (
//   url?: string,
//   type?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg',
//   convertSVGToImage?: boolean
// ) => {
//   if (
//     type &&
//     url &&
//     !url.includes('/master/') &&
//     !url.includes('/video/') &&
//     (url.includes('/category/') ||
//       url.includes('/sub-category/') ||
//       url.includes('/avatar/') ||
//       url.includes('/logo/') ||
//       url.includes('/product/') ||
//       url.includes('/area/') ||
//       url.includes('/branch/') ||
//       url.includes('/product-object/') ||
//       url.includes('/banner/') ||
//       url.includes('/gallery/') ||
//       url.includes('/blog/') ||
//       url.includes('/certification/') ||
//       url.includes('/image/'))
//   ) {
//     const index = url.lastIndexOf('.');
//     return `${url.slice(0, index)}_s${ImageSize[type]}.${url.slice(index + 1)}`;
//   }

//   if (convertSVGToImage) {
//     if (url?.includes("ui-avatars")) {
//       return `${url}&format=png`;
//     }
//   }
//   return url || '';
// };

export const getImportTimeAsText = (
  importSettings: ProposalPolicyImportSettings | PricePolicyImportSettings
) => {
  const { type, time, isAutoImportEnabled } = importSettings || {};
  const timeFormat = 'HH:mm';

  if (type == undefined || time == undefined || isAutoImportEnabled != true) {
    return '';
  }

  const prefix = 'Cập nhật lúc';

  switch (type) {
    case IMPORT_TYPE_VALUE.SPECIFIC_IMPORT:
      return `${prefix} ${formatDate(time?.absoluteTime, 'DD/MM/YYYY HH:mm')}`;
    case IMPORT_TYPE_VALUE.DATE_IMPORT:
      return time?.hours != undefined && time?.minutes != undefined
        ? `${prefix} ${moment(`${time.hours}:${time.minutes}`, 'HH:mm').format(
          timeFormat
        )} mỗi ngày`
        : 'Chưa cập nhật';
    case IMPORT_TYPE_VALUE.WEEK_IMPORT: {
      let weekText = '';
      const dayOfWeekText = time?.dayOfWeek
        ? DAY_OF_WEEK[time.dayOfWeek - 1].name
        : '';
      const dateText =
        time?.hours != undefined && time?.minutes != undefined
          ? moment(`${time.hours}:${time.minutes}`, 'HH:mm').format(timeFormat)
          : '';
      if (dateText) {
        weekText += dateText;
        if (dayOfWeekText) {
          weekText += ` mỗi ${dayOfWeekText}`;
        }
      }
      return `${prefix} ${weekText}` || 'Chưa cập nhật';
    }
    case IMPORT_TYPE_VALUE.MONTH_IMPORT: {
      let monthText = '';
      const dayOfMonthText = time?.date || '';
      const dateText =
        time?.hours != undefined && time?.minutes != undefined
          ? moment(`${time.hours}:${time.minutes}`, 'HH:mm').format(timeFormat)
          : '';
      if (dateText) {
        monthText += dateText;
        if (dayOfMonthText) {
          monthText += ` ngày ${dayOfMonthText} mỗi tháng`;
        }
      }
      return `${prefix} ${monthText}` || 'Chưa cập nhật';
    }
    default:
      return 'Chưa cập nhật';
  }
};

export const updateStyleElement = (
  className: string,
  attr: string,
  value: string
) => {
  if (document) {
    const elements = document.getElementsByClassName(className);

    for (let i = 0; i < elements.length; i++) {
      const slide = elements[i] as HTMLElement;

      if (slide.style[attr] != value) {
        slide.style[attr] = value;
      }
    }
  }
};

export const updateAttrElement = (
  className: string,
  attr: string,
  value: string
) => {
  if (document) {
    const elements = document.getElementsByClassName(className);

    for (let i = 0; i < elements.length; i++) {
      const slide = elements[i] as HTMLElement;

      if (slide[attr] != value) {
        slide[attr] = value;
      }
    }
  }
};

export const replaceItemsInList = (
  items: any[],
  list: any[],
  setList?: (e: any[]) => void,
  key?: string
) => {
  const newList = deepClone(list);
  for (const item of items) {
    const fieldKey = key ? key : "id"
    const index = list.findIndex(e => e[fieldKey] === item[fieldKey]);
    if (index !== -1) {
      newList[index] = {
        ...newList[index],
        ...item,
      };

    }
  }
  setList && setList(newList);
  return newList;

};

export const replaceItemInList = (
  item: any,
  list: any[],
  setList?: (e: any[]) => void
) => {
  const index = list.findIndex(e => e.id === item.id);
  if (index !== -1) {
    const newList = deepClone(list);

    newList[index] = {
      ...newList[index],
      ...item,
    };

    setList && setList(newList);
    return newList;
  }

  return [];
};

export const replaceItemInListOptimize = (
  item: any,
  setList: (e: (prevList: any[]) => any[]) => void
) => {
  setList((prevList: any[]) => {
    const index = prevList.findIndex(e => e.id === item.id);
    if (index !== -1) {
      const newList = [...prevList];
      newList[index] = {
        ...newList[index],
        ...item,
      };
      return newList;
    }
    return prevList;
  });
};

export const replaceEntityProductItemInlistWithProduct = (
  item: any,
  list: any[],
  setList?: (e: any[]) => void
) => {
  const index = list.findIndex(e => e.product.id === item.id);
  if (index !== -1) {
    const newList = deepClone(list);

    newList[index].product = {
      ...newList[index].product,
      ...item,
    };
    setList && setList(newList);
    return newList;
  }

  return [];
};

export const formatInfoBox = item => {
  return {
    ...item,
    key: item.id,
    value: item.id,
    infoBox: {
      name: item.name,
      img: item.avatar,
    },
  };
};

export const getReportQuantityAsText = (reportInfo: {
  reportType: number;
  relativeQuantity: number;
  absoluteQuantity: number;
}) => {
  if (!reportInfo) {
    return 0;
  }
  const { reportType, relativeQuantity, absoluteQuantity } = reportInfo;
  switch (reportType) {
    case 0:
      switch (relativeQuantity) {
        case 0:
          return 'Hết hàng';
        case 1:
          return 'Có ít';
        case 2:
          return 'Có vừa';
        case 3:
          return 'Có nhiều';
        default:
          return 0;
      }
    case 1:
      return absoluteQuantity;
    default:
      return 0;
  }
};

export const getReportQuantity = (
  quantityAsText: string
): {
  reportType: number;
  relativeQuantity: number;
  absoluteQuantity: number;
} => {
  if (!quantityAsText || quantityAsText.trim() === '') {
    return {
      reportType: REPORT_TYPE.EMPTY,
      relativeQuantity: PRODUCT_RELATIVE_QUANTITY.NONE,
      absoluteQuantity: 0,
    };
  }
  if (isNaN(Number(quantityAsText))) {
    let relativeQuantity;
    const formatText = quantityAsText.trim().toLowerCase();
    switch (formatText) {
      case 'có ít':
        relativeQuantity = PRODUCT_RELATIVE_QUANTITY.FEW;
        break;
      case 'có vừa':
        relativeQuantity = PRODUCT_RELATIVE_QUANTITY.MEDIUM;
        break;
      case 'có nhiều':
        relativeQuantity = PRODUCT_RELATIVE_QUANTITY.MANY;
        break;
      case 'hết hàng':
        relativeQuantity = PRODUCT_RELATIVE_QUANTITY.NONE;
        break;
      default:
        relativeQuantity = PRODUCT_RELATIVE_QUANTITY.NONE;
        return {
          reportType: REPORT_TYPE.EMPTY,
          relativeQuantity,
          absoluteQuantity: 0,
        };
    }
    return {
      reportType: REPORT_TYPE.RELATIVE,
      relativeQuantity,
      absoluteQuantity: 0,
    };
  }
  return {
    reportType: REPORT_TYPE.ABSOLUTE,
    relativeQuantity: PRODUCT_RELATIVE_QUANTITY.NONE,
    absoluteQuantity: Number(quantityAsText),
  };
};

export const getLastPage = (
  paginationTake: number,
  paginationTotal: number,
  arrDeletes: any[]
) => Math.ceil((paginationTotal - arrDeletes?.length) / paginationTake);

export const isLastPage = (
  paginationCurrent: number,
  paginationTake: number,
  paginationTotal: number
) =>
  paginationCurrent > 1 &&
  Math.ceil(paginationTotal / paginationTake) == paginationCurrent;

export const getPreviousPath = (): string | null => {
  if (typeof window !== 'undefined') {
    return sessionStorage.getItem('prevPath');
  }
  return null;
};

export const hasLoadLastPagePagination = (
  newList: any[],
  pagination: { current: number; take: number; total: number }
) =>
  newList?.length === 0 &&
  isLastPage(pagination.current, pagination.take, pagination.total);

export const hasLoadLastPage = (
  newList: any[],
  paginationCurrent: number,
  paginationTake: number,
  paginationTotal: number
) =>
  newList?.length === 0 &&
  isLastPage(paginationCurrent, paginationTake, paginationTotal);

export const checkLoadPageWithID = (
  savedList: any[],
  route: string,
  prefix?: string,
  postfix?: string
) =>
  savedList.length > 0 &&
  new RegExp(
    `${prefix}[0-9a-fA-F]{8}\\b-[0-9a-fA-F]{4}\\b-[0-9a-fA-F]{4}\\b-[0-9a-fA-F]{4}\\b-[0-9a-fA-F]{12}${postfix}`,
    'g'
  ).test(route);

export const handleUpdateOptions = (
  list,
  setList,
  typeID: string,
  options: any[],
  deletedOptionIDs?: string[],
  typeOptions?: any[],
  type?:
    | 'productOptions'
    | 'orderOptions'
    | 'entityOptions'
    | 'product.productOptions'
): void => {
  const formatOptions = options.reduce((result, item) => {
    result[item.id] = { ...item };
    return result;
  }, {});

  if (type === 'product.productOptions') {
    const newList = list.map(item => {
      if (typeID === item.product.id && typeOptions) {
        return {
          ...item,
          product: {
            ...item.product,
            productOptions: typeOptions || [],
          },
        };
      }

      const newTypeOptions = item.product.productOptions
        .filter(item => !(deletedOptionIDs || []).includes(item.option?.id))
        .map(item => {
          return {
            ...item,
            option: formatOptions[item.option?.id] || item.option,
          };
        });

      return {
        ...item,
        product: {
          ...item.product,
          productOptions: newTypeOptions || [],
        },
      };
    });

    setList(newList);
  } else {
    const newList = list.map(item => {
      if (typeID === item.id && typeOptions) {
        return {
          ...item,
          [type || '']: typeOptions || [],
        };
      }

      const newTypeOptions = item[type || '']
        .filter(item => !(deletedOptionIDs || []).includes(item.option?.id))
        .map(item => {
          return {
            ...item,
            option: formatOptions[item.option?.id] || item.option,
          };
        });

      return {
        ...item,
        [type || '']: newTypeOptions,
      };
    });

    setList(newList);
  }
};

export const deepClone = (data: any) => JSON.parse(JSON.stringify(data));

export const genPartnerCodeFromEntityCode = (code: string, length?: number) => {
  if (code) {
    const count = length || 10;
    const index_1 = code.indexOf('-');
    return (
      code
        .slice(index_1 + 1, index_1 + count + 2)
        .replace('-', '')
        .trim()
        .slice(0, count - 2) + code.slice(-2)
    );
  }
};
export const disableEvent = (className: string, value: string) => {
  if (document) {
    const elements = document.getElementsByClassName(className);

    for (let i = 0; i < elements.length; i++) {
      const slide = elements[i] as HTMLElement;
      slide.style.pointerEvents = value;
    }
  }
};

export const chunkArrayIntoGroups = (array: any[], groupSize: number) => {
  const myArray: any[] = [];
  for (let i = 0; i < array.length; i += groupSize) {
    myArray.push(array.slice(i, i + groupSize));
  }

  return myArray;
};

export const formatOrderItemsForTableList = ({
  orderLogs,
  orderItems,
  orderType,
  orderCustomerID,
  orderStatus,
  trackingStatus,
  currentEntityID,
  productUnit,
}: {
  orderLogs: any[];
  orderItems: any[];
  orderType: number;
  orderCustomerID: string;
  orderStatus: number;
  trackingStatus: any[];
  currentEntityID: string;
  productUnit: any;
}) => {
  const deletedOrderLogs = orderLogs.filter(
    orderItem => orderItem.action === ORDER_ACTION_ENUM.DELETE_PRODUCT
  );
  const isExistDeletedOrderItems = deletedOrderLogs?.length > 0;
  const deletedOrderItems: any[] = [];
  if (isExistDeletedOrderItems) {
    const deletedProductsOrderItems = deletedOrderLogs.map(
      item => item.additionalInfo.deleteProducts
    );
    deletedProductsOrderItems?.forEach((orderItem: any) => {
      orderItem.forEach(deleteProductLog => {
        deletedOrderItems.push({
          ...deleteProductLog,
          product: {
            id: deleteProductLog.productID,
            name: deleteProductLog.productName,
            code: deleteProductLog.productSKU,
            avatar: deleteProductLog.productAvatar,
          },
          customerProduct: {
            id: deleteProductLog.customerProductID,
            name: deleteProductLog.customerProductName,
            code: deleteProductLog.customerProductSKU,
            avatar: deleteProductLog.customerProductAvatar,
          },
          isDeletedOrder: true,
        });
      });
    });
  }

  // clone orderItems
  let formattedOrderItems = [...orderItems, ...deletedOrderItems].map(item => {
    return { ...item, orderType };
  });

  if (
    orderCustomerID === currentEntityID &&
    isWholesaleOrder(orderType) &&
    formattedOrderItems.length
  ) {
    formattedOrderItems.forEach(item => {
      const { customerQuantityObj, customerOriginPrice, customerPrice } =
        getCustomerReportQuantityAndPrice({
          customerWholesaleUnit: item.customerProductWholesaleUnit?.postfix
            ? item.customerProductWholesaleUnit
            : item.supplierProductWholesaleUnit,
          supplierWholesaleUnit: item.supplierProductWholesaleUnit,
          productUnitPostfixObj: productUnit.postfixObj,
          supplierQuantityObj: {
            customerQuantity: item.customerQuantity,
            supplierQuantity: item.supplierQuantity,
            supplierDeliveredQuantity: item.supplierDeliveredQuantity,
            customerReceivedQuantity: item.customerReceivedQuantity,
          },
          supplierOriginPrice: item.originPrice,
          supplierPrice: item.price,
        });
      item.customerQuantity = customerQuantityObj.customerQuantity;
      item.supplierQuantity = customerQuantityObj.supplierQuantity;
      item.supplierDeliveredQuantity =
        customerQuantityObj.supplierDeliveredQuantity;
      item.customerReceivedQuantity =
        customerQuantityObj.customerReceivedQuantity;
      item.originPrice = customerOriginPrice;
      item.price = customerPrice;
    });
  }

  formattedOrderItems = formatOrderItemsQuantitiesByOrderStatus(
    formattedOrderItems,
    orderStatus,
    trackingStatus
  );
  return formattedOrderItems;
};

export function removeOrderFromUpdateStatusByCustomerQueue(
  order: any,
  queue: any
) {
  const newQueue = { ...queue };

  const orderIndex = newQueue[order.customerID].findIndex(
    item => item.id === order.id
  );
  newQueue[order.customerID].splice(orderIndex, 1); // remove order from array order by supplier

  if (newQueue[order.customerID].length === 0) {
    delete newQueue[order.customerID]; // delete key if there is no order
  }

  return newQueue;
}

export const getSelectedCentralEntityID = (accountList, centralEntityID) => {
  const centralEntities = [...accountList].filter(item => item.isCentral);
  let selectedCentralEntityID = null;
  if (centralEntities.length > 1) {
    if (centralEntityID) {
      const found = centralEntities.find(item => item.id === centralEntityID);
      selectedCentralEntityID = found.id;
    } else {
      const found = centralEntities.find(item => item.role === 'owner');
      selectedCentralEntityID = found?.id;
    }
  } else if (centralEntities.length === 1) {
    if (centralEntityID) {
      if (centralEntities[0].id === centralEntityID) {
        selectedCentralEntityID = centralEntities[0].id;
      }
    } else {
      if (centralEntities[0].role === 'owner') {
        selectedCentralEntityID = centralEntities[0].id;
      }
    }
  }
  return selectedCentralEntityID;
};

export const getTextInsideParenthese = (text: string): string => {
  const regExp = /\(([^)]+)\)/;
  const matches = regExp.exec(text);
  return matches ? matches[1] : '';
};

export const formatMoneyFromText = (
  price?: string,
  locale = 'en_US'
): number => {
  if (locale === 'vi_VN') {
    return Number(
      price?.toString()?.replace(/[.\s]/g, "")?.replace(',', '.')
    );
  }
  return Number(price?.toString()?.replace(/[,\s]/g, "") || '');
};

export const buildPagination = (page: number, take: number) => {
  const buildPage = page <= 0 ? 1 : page;
  const buildTake = take <= 0 ? 10 : take;
  return {
    current: buildPage,
    next: buildPage + 1,
    prev: buildPage - 1,
    take: buildTake,
    total: 0,
  };
};

export const convertSheetToArrayBuffer = (sheet): ArrayBuffer => {
  const buf = new ArrayBuffer(sheet.length);

  const view = new Uint8Array(buf);

  for (let i = 0; i !== sheet.length; ++i) {
    view[i] = sheet.charCodeAt(i);
  }
  return buf;
};

export const convertWorkBookToBlob = (workbook): Blob => {
  const wbout = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'binary',
  });

  const blob = new Blob([convertSheetToArrayBuffer(wbout)], {
    type: 'application/octet-stream',
  });

  return blob;
};

export const groupBy = (
  data: any[],
  getGroupByKey: (data: any) => string
): Record<string, any[]> => {
  const groups = data.reduce((result, item) => {
    const groupValue = getGroupByKey(item);
    if (!result[groupValue]) {
      result[groupValue] = [];
    }
    result[groupValue].push(item);
    return result;
  }, {});

  return groups;
};

export const getDurationCronjobTime = (startTime: string, endTime: string) => {
  const startTimeValue = moment(startTime).format("HH:mm:ss");
  const endTimeValue = moment(endTime).format("HH:mm:ss");
  const startAt = moment(startTimeValue, "HH:mm:ss");
  const endAt = moment(endTimeValue, "HH:mm:ss");

  const duration = Math.abs(startAt.diff(endAt, "minutes"));
  if (duration) {
    return duration;
  }
  return -1;
}

export const checkIsCrossDay = (startTime: string, endTime: string) => {
  try {
    if (!startTime || !endTime) {
      return false
    }
    const startedAtValue = moment(startTime).format("HH:mm:ss");
    const startedAt = moment(startedAtValue, "HH:mm:ss");

    const endedAtValue = moment(endTime).format("HH:mm:ss");
    const endedAt = moment(endedAtValue, "HH:mm:ss");

    if (startedAt.isBefore(endedAt)) {
      return false;
    }
    return true;
  } catch (error) {
    throw error;
  }
}

export const buildOrdinalColumn = (
  index: number,
  page?: number,
  take?: number
): number => {
  if (page != undefined && take != undefined) {
    const { current, take: pageSize } = buildPagination(page, take);
    return (current - 1) * pageSize + index + 1;
  }
  return index + 1;
};


export const getLastWord = (
  value: string
) => {
  return value.trim().split(' ').pop();
};

export const handleRedirectSignIn = (history: any, path: string, isSpecificBrowser: boolean, domain: string) => {
  if (typeof window !== 'undefined') {
    if (isSpecificBrowser) {
      window.open(
        `${domain ?? env.service.hubidentityWebApp}${path}?continue=${encodeURIComponent(
          `${window.location.origin}${window.location.pathname}`
        )}`, "_self"
      );
    } else {
      handleRedirectPopupSignIn(history, path, domain);
    }
  }
};

export const validatePolicyCode = (code: string): boolean => {
  if (code != undefined) {
    if (
      POLICY_CODE.MIN_LENGTH <= code.length &&
      code.length <= POLICY_CODE.MAX_LENGTH
    ) {
      return true;
    }
    return false;
  }
  return false;
};

export const handleRedirectZaloOA = (appID: any, redirect_uri: string) => {
  if (window) {
    const myWindow = window.open(
      `https://oauth.zaloapp.com/v4/oa/permission?app_id=${appID}&redirect_uri=${redirect_uri}`, '_self'
      ,
    );
  }
};

export const handleRedirectPopupSignIn = (history: any, path: string, domain: string) => {
  setTimeout(() => {
    if (window) {
      const myWindow = window.open(
        `${domain ?? env.service.hubidentityWebApp}${path}?isPop=wClose&continue=${encodeURIComponent(
          `${window.location.origin}${window.location.pathname}`
        )}`,
        '_blank',
        'popup=yes,toolbar=yes,scrollbars=yes,resizable=yes'
      );

      myWindow?.postMessage('message', `${window.location.origin}${window.location.pathname}`);

      const response = (event: any) => {
        if (event!.origin == (domain ?? env.service.hubidentityWebApp)) {
          if (JSON.parse(event.data)) {
            const data = JSON.parse(event.data);
            if (Object.keys(data).includes('USER_ID') && data?.USER_ID) {
              localStorage.setItem(USER_ID, data.USER_ID);
            }

            if (Object.keys(data).includes('REFRESH_TOKEN') && data?.REFRESH_TOKEN) {
              localStorage.setItem(REFRESH_TOKEN, data.REFRESH_TOKEN);
            }

            localStorage.removeItem(SELECTED_ENTITY);
            window.location.reload();
          }
        }
      };

      window.addEventListener('message', response);

      if (myWindow) {
        const timer = setInterval(() => {
          if (myWindow.closed) {
            clearInterval(timer);
            window.removeEventListener('message', response);
          }
        }, 1000);
      }
    }
  }, 1);
};

export const detectIncognito = function (): Promise<{
  isPrivate: boolean;
  browserName: string;
}> {
  return new Promise((resolve, reject) => {
    let browserName = 'Unknown';

    function __callback(isPrivate: any): void {
      resolve({
        isPrivate,
        browserName,
      });
    }

    function identifyChromium(): string {
      const ua = navigator.userAgent;
      if (ua.match(/Chrome/)) {
        if ((navigator as any).brave !== undefined) {
          return 'Brave';
        }
        if (ua.match(/Edg/)) {
          return 'Edge';
        }
        if (ua.match(/OPR/)) {
          return 'Opera';
        }
        return 'Chrome';
      }
      return 'Chromium';
    }

    function assertEvalToString(value: any): boolean {
      // eslint-disable-next-line no-eval
      return value === eval.toString().length;
    }

    function isSafari(): boolean {
      const v = navigator.vendor;
      return v !== undefined && v.indexOf('Apple') === 0 && assertEvalToString(37);
    }

    function isChrome(): boolean {
      const v = navigator.vendor;
      return v !== undefined && v.indexOf('Google') === 0 && assertEvalToString(33);
    }

    function isFirefox(): boolean {
      return (
        document.documentElement !== undefined &&
        (document as any).documentElement.style.MozAppearance !== undefined &&
        assertEvalToString(37)
      );
    }

    function isMSIE(): boolean {
      return (navigator as any).msSaveBlob !== undefined && assertEvalToString(39);
    }

    /**
     * Safari (Safari for iOS & macOS)
     * */

    // eslint-disable-next-line consistent-return
    function newSafariTest(): void {
      const tmp_name = String(Math.random());

      try {
        const db = window.indexedDB.open(tmp_name, 1);

        // eslint-disable-next-line func-names, consistent-return
        db.onupgradeneeded = function (i) {
          const res = db?.result;

          try {
            res
              .createObjectStore('test', {
                autoIncrement: true,
              })
              .put(new Blob());

            __callback(false);
          } catch (e) {
            let message = e;

            if (e instanceof Error) {
              message = e.message ?? e;
            }

            if (typeof message !== 'string') {
              return __callback(false);
            }

            const matchesExpectedError = /BlobURLs are not yet supported/.test(message);

            return __callback(matchesExpectedError);
          } finally {
            res.close();
            window.indexedDB.deleteDatabase(tmp_name);
          }
        };
      } catch (e) {
        return __callback(false);
      }
    }

    function oldSafariTest(): void {
      const openDB = (window as any).openDatabase;
      const storage = window.localStorage;
      try {
        openDB(null, null, null, null);
      } catch (e) {
        return __callback(true);
      }
      try {
        storage.setItem('test', '1');
        storage.removeItem('test');
      } catch (e) {
        return __callback(true);
      }
      return __callback(false);
    }

    function safariPrivateTest(): void {
      if (navigator.maxTouchPoints !== undefined) {
        newSafariTest();
      } else {
        oldSafariTest();
      }
    }

    /**
     * Chrome
     * */

    function getQuotaLimit(): number {
      const w = window as any;
      if (
        w.performance !== undefined &&
        w.performance.memory !== undefined &&
        w.performance.memory.jsHeapSizeLimit !== undefined
      ) {
        return (performance as any).memory.jsHeapSizeLimit;
      }
      return 1073741824;
    }

    // >= 76
    function storageQuotaChromePrivateTest(): void {
      (navigator as any).webkitTemporaryStorage.queryUsageAndQuota(
        (_: number, quota: number) => {
          const quotaInMib = Math.round(quota / (1024 * 1024));
          const quotaLimitInMib = Math.round(getQuotaLimit() / (1024 * 1024)) * 2;

          __callback(quotaInMib < quotaLimitInMib);
        },
        (e: any) => {
          reject(new Error(`detectIncognito somehow failed to query storage quota: ${e.message}`));
        }
      );
    }

    // 50 to 75
    function oldChromePrivateTest(): void {
      const fs = (window as any).webkitRequestFileSystem;
      const success = function () {
        __callback(false);
      };
      const error = function () {
        __callback(true);
      };
      fs(0, 1, success, error);
    }

    function chromePrivateTest(): void {
      if (self.Promise !== undefined && (self.Promise as any).allSettled !== undefined) {
        storageQuotaChromePrivateTest();
      } else {
        oldChromePrivateTest();
      }
    }

    /**
     * Firefox
     * */

    function firefoxPrivateTest(): void {
      __callback(navigator.serviceWorker === undefined);
    }

    /**
     * MSIE
     * */

    function msiePrivateTest(): void {
      __callback(window.indexedDB === undefined);
    }

    function main(): void {
      if (isSafari()) {
        browserName = 'Safari';
        safariPrivateTest();
      } else if (isChrome()) {
        browserName = identifyChromium();
        chromePrivateTest();
      } else if (isFirefox()) {
        browserName = 'Firefox';
        firefoxPrivateTest();
      } else if (isMSIE()) {
        browserName = 'Internet Explorer';
        msiePrivateTest();
      } else {
        reject(new Error('detectIncognito cannot determine the browser'));
      }
    }

    main();
  });
};

export const getConfirmOrderData = ({ order, productUnit, onError, index = 0 }: {
  index?: number,
  order: any, productUnit: any, onError: any
}): any => {
  let summaryOrderData = {
    deliveryFee: 0,
    totalPrice: BigInt(0),
    totalTemporaryPrice: BigInt(0),
    totalItem: 0,
    totalAdjustment: BigInt(0),
    adjustments: [],
    orderID: ''
  };

  let summaryCartInformationData = {
    paymentMethod: 0,
    deliveryMethod: 0,
    note: '',
    customerNote: '',
    supplierNote: '',
    address: '',
    totalCustomerQuantity: 0,
  };

  let confirmProductsData: any[] = [];

  try {

    summaryOrderData = {
      deliveryFee: order.deliveryFee,
      totalPrice: order.totalPrice,
      totalTemporaryPrice: order?.totalPrice - order?.deliveryFee + order?.totalAdjustment,
      totalItem: order.totalItem,
      totalAdjustment: order.totalAdjustment,
      adjustments: [],
      orderID: order.id
    };

    summaryCartInformationData = {
      paymentMethod: order.paymentMethod,
      deliveryMethod: order.deliveryMethod,
      note: order.customerNote,
      supplierNote: order.supplierNote,
      customerNote: order.customerNote,
      address: order.additionalInfo.address
        ? `(${formatAppearPhoneNumber(
          order.additionalInfo.address?.phoneNumber
        )}) ${getFullAddress(
          order.additionalInfo.address?.address,
          order.additionalInfo.address?.ward?.district?.fullName ??
          order.additionalInfo.address?.district?.fullName,
          order.additionalInfo.address?.ward?.province?.fullName ??
          order.additionalInfo.address?.province?.fullName,
          order.additionalInfo.address?.ward?.fullName
        )}`
        : '',
      totalCustomerQuantity: order.totalItem,
    };

    const isWholesale = isWholesaleOrder(order.type)

    confirmProductsData = [
      ...sortProductByUpdatedAt(
        order.orderItems
          .map(item => {
            return {
              avatar: item.product.avatar,
              name: item.product.name,
              unit: buildProductWholesaleUnit({
                unitPrefix: productUnit.prefixObj[(isWholesale ? item.supplierProductWholesaleUnit?.prefix : item.supplierProductRetailUnit?.prefix) || ''],
                unitWeight: (isWholesale ? item.supplierProductWholesaleUnit?.weight : item.supplierProductRetailUnit?.weight) || 0,
                unitPostfix:
                  productUnit.postfixObj[(isWholesale ? item.supplierProductWholesaleUnit?.postfix : item.supplierProductRetailUnit?.postfix) || ''],
              }),
              quantity: item.customerQuantity,
              originPrice: item.supplierProductWholesaleUnit?.price || 0,
              price: item.price || 0,
              customerNote: item.customerNote,
              supplierNote: item.supplierNote,
              productOptions: item.productOptions || [],
              originCustomerQuantity: 0,
              supplierQuantity: item.supplierQuantity,
              originUnit: buildProductWholesaleUnit({
                unitPrefix:
                  productUnit.prefixObj[(isWholesale ? item?.supplierProductWholesaleUnit?.prefix : item?.supplierProductRetailUnit?.prefix) || ''],
                unitWeight: (isWholesale ? item?.supplierProductWholesaleUnit?.weight : item?.supplierProductRetailUnit?.weight) || 0,
                unitPostfix:
                  productUnit.postfixObj[(isWholesale ? item?.supplierProductWholesaleUnit?.postfix : item?.supplierProductRetailUnit?.prefix) || ''],
              }),
            };
          })
          .filter(item => item.quantity > 0)
      ),
    ];
  } catch (error: any) {
    onError(error)
    Notification('error', 'Tải thông tin đơn hàng thất bại', error);
  }

  return {
    index,
    order,
    summaryOrderData,
    confirmProductsData,
    summaryCartInformationData,
  };
};

export const getBase64Image = (image: any) => {
  var canvas = document.createElement("canvas");
  canvas.width = image.width;
  canvas.height = image.height;
  var ctx: any = canvas.getContext("2d");
  ctx.drawImage(image, 0, 0);
  var dataURL = canvas.toDataURL("image/png");
  return dataURL
}

export const viewBill = (code: any, domain: string) => {
  window.open(`${domain ?? env.service.hubbillingWebApp}/${code}`);
};

export const copyBillLink = (code: any, domain: string) => {
  const billLink = `${domain ?? env.service.hubbillingWebApp}/${code}`;
  copyLink(billLink, 'đường dẫn');
}

export const handleExportImage = async ({
  imageName,
  imageType,
  ref,
}: {
  imageName: string;
  imageType: string;
  ref: any;
}) => {
  const nameExport = imageName || `QR`;
  const element: any = ref.current;
  const canvas = await html2canvas(element, {
    allowTaint: true,
    useCORS: true,
    scale: 2,
  });
  const data = canvas.toDataURL(`${nameExport}/${imageType}`);
  const link = document.createElement('a');
  link.href = data;
  link.download = buildExportFileName(nameExport);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const generateFacebookShareLink = (link: string | undefined): string =>
  link ? `https://www.facebook.com/sharer/sharer.php?u=${link}` : '';

export const generateFacebookMessengerShareLink = (link: string | undefined): string =>
  link
    ? `https://www.facebook.com/dialog/send?link=${link}&app_id=2244921662331576&redirect_uri=https://www.messenger.com`
    : '';

export const generatePinterestShareLink = (link: string | undefined): string =>
  link ? ` https://pinterest.com/pin/create/button/?url=${link}&media=&description=` : '';

export const generateTelegramShareLink = (link: string | undefined): string =>
  link ? ` https://telegram.me/share/url?url=${link}` : '';

export const removeAuthRouter = (history: any) => {
  const originRedirect = history.location.search;
  const isRedirect = originRedirect.split('?').length > 1;
  const newRedirect =
    originRedirect.split('?').length > 1
      ? `${originRedirect.split('?')[0]}${originRedirect
        .split('?')[1]
        .split('&')
        .filter((item: any) => !(item.includes('userID') || item.includes('refresh') || item.includes('entity') || item.includes('productObjectID'))).length > 0
        ? '?'
        : ''
      }${originRedirect
        .split('?')[1]
        .split('&')
        .filter((item: any) => !(item.includes('userID') || item.includes('refresh') || item.includes('entity') || item.includes('productObjectID')))}`
      : originRedirect.split('?')[0];

  if (isRedirect) {
    history.replace(history.location.pathname + newRedirect);
  }
};

export const convertIntObj = (obj: any) => {
  const res = {}
  for (const key in obj) {
    res[key] = Number(obj[key])
  }
  return res;
}

export const getDirectLink = (pathName: string) => {
  const data = [
    {
      route: '/sale/quick-forecast',
      redirect: 'sale/quick-forecast',
    },
    {
      route: '/sale/#',
      redirect: '/sale/#',
    },
    {
      route: '/sale/balance',
      redirect: 'sale/balance',
    },
    {
      route: '/purchase/order-chain-supplier',
      redirect: 'purchase/order-chain-supplier',
    },
  ];

  const dataFind = data.find((item: any) => item?.route == pathName);

  if (dataFind) {
    return dataFind?.redirect || '';
  }

  return '';
};

export const isUUID = (str) => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

export const generateUUID = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const buildExportFileName = (name: string) => {
  let formatName = name?.trim() ? removeVietnameseTones(name.trim().toLowerCase()) : "bao_cao";
  formatName = removeDuplicateSpaceString(formatName.trim()).replace(/ /g, "_")
  formatName = formatName.concat(`_${Date.now().toString()}`)
  return formatName;
}

export const renderHtml = (str: string) => {
  if (!str) {
    return 'Trống';
  }

  return str.replace(/\n/g, '<br />');
};

export const getPartnerTitle = (type: number, isShortName = false) => {
  switch (type) {
    case PARTNER_TYPE.BRANCH:
      return isShortName ? 'CNCH' : 'Chi nhánh cửa hàng';

    case PARTNER_TYPE.CUSTOMER:
      return isShortName ? 'ĐT' : 'Đối tác';

    case PARTNER_TYPE.SUPPLIER:
      return isShortName ? 'NCC' : 'Nhà cung cấp';

    case PARTNER_TYPE.WAREHOUSE:
      return isShortName ? 'CNSX' : 'Chi nhánh sản xuất';

    case PARTNER_TYPE.CENTRAL:
      return isShortName ? 'Trung tâm' : 'Trung tâm';


    default:
      return '';
  }
}

export const generateSuggestEntityType = (partnerType, entityType, isHasDisabledField = false, isHasAllOption = true) => {
  if ([PARTNER_TYPE.CUSTOMER, PARTNER_TYPE.BRANCH].includes(partnerType)) {
    return generateEntityConnectForCustomer(entityType, isHasDisabledField, isHasAllOption)
  }

  return generateEntityConnectForSupplier(entityType, isHasDisabledField, isHasAllOption)

}

export const generateEntityConnectForSupplier = (type, isHasDisabledField, isHasAllOption) => {
  const result: any[] = []
  if (type == ENTITY_TYPE.FARM) {

    result.push(
      {
        value: ENTITY_TYPE.FARM,
        name: 'Nông trại',
        label: 'Nông trại',
      }
    )
    if (isHasDisabledField) {
      result.push(
        ...[
          {
            value: ENTITY_TYPE.STORE,
            name: 'Cửa hàng',
            label: 'Cửa hàng',
            disabled: true
          },
          {
            value: ENTITY_TYPE.COLLABORATOR,
            name: 'Đại sứ bán hàng',
            label: 'Đại sứ bán hàng',
            disabled: true
          },
        ]
      )
    }
  }
  if (type == ENTITY_TYPE.STORE) {
    result.push(...[{
      value: ENTITY_TYPE.FARM,
      name: 'Nông trại',
      label: 'Nông trại',
    },
    {
      value: ENTITY_TYPE.STORE,
      name: 'Cửa hàng',
      label: 'Cửa hàng',
    },])
    if (isHasDisabledField) {
      result.push(
        {
          value: ENTITY_TYPE.COLLABORATOR,
          name: 'Đại sứ bán hàng',
          label: 'Đại sứ bán hàng',
          disabled: true
        },
      )
    }
  }

  if (isHasAllOption) {
    result.unshift({
      label: 'Tất cả',
      value: PARTNER_IN_CHAIN_TYPE.ALL,
    })
  }
  return result
}

export const generateEntityConnectForCustomer = (type, isHasDisabledField, isHasAllOption) => {
  let result: any[] = []
  if (type == ENTITY_TYPE.FARM) {
    result = [
      {
        value: ENTITY_TYPE.FARM,
        name: 'Nông trại',
        label: 'Nông trại',
      },
      {
        value: ENTITY_TYPE.STORE,
        name: 'Cửa hàng',
        label: 'Cửa hàng',
      },
      {
        value: ENTITY_TYPE.COLLABORATOR,
        name: 'Đại sứ bán hàng',
        label: 'Đại sứ bán hàng',
      },
    ];
  }
  if (type == ENTITY_TYPE.STORE) {
    result = [
      {
        value: ENTITY_TYPE.STORE,
        name: 'Cửa hàng',
        label: 'Cửa hàng',
      },
      {
        value: ENTITY_TYPE.COLLABORATOR,
        name: 'Đại sứ bán hàng',
        label: 'Đại sứ bán hàng',
      },
    ];
    if (isHasDisabledField) {
      result.unshift(
        {
          value: ENTITY_TYPE.FARM,
          name: 'Nông trại',
          label: 'Nông trại',
          disabled: true
        },
      )
    }
  }
  if (type == ENTITY_TYPE.COLLABORATOR) {
    result = [
    ];
  }

  if (isHasAllOption) {
    result.unshift(
      {
        // name: 'Tất cả',
        label: 'Tất cả',
        value: PARTNER_IN_CHAIN_TYPE.ALL,
      },
    )
  }
  return result;
}

export const generateNextStepUpdateChainOrder = (currentStatus: number): NextUpdateStatusChainOrder | null | undefined => {
  const foundCurrentStatusIndex = FLOW_CHAIN_UPDATE_STATUS.findIndex(item => item === currentStatus)
  const oldStatus = currentStatus;


  // Kiểm tra tồn tại và không phải là phần tử cuối
  if (foundCurrentStatusIndex != -1 && foundCurrentStatusIndex != FLOW_CHAIN_UPDATE_STATUS.length - 1) {
    const newStatus = FLOW_CHAIN_UPDATE_STATUS[foundCurrentStatusIndex + 1]
    const id = newStatus
    const tooltip = CHAIN_ORDER_STATUS_TEXT[newStatus]


    if (newStatus == ORDER_STATUS.PENDING_EXECUTE) {
      return {
        code: 'PENDING_EXECUTE',
        icon: <PendingExecuteIcon className="icon" width={18} height={18} />,
        tooltip,
        id: newStatus,
        newStatus,
        oldStatus,
        shouldOpenDeliveredModal: false,
        shouldOpenReceivedModal: false
      }
    }

    if (newStatus == ORDER_STATUS.PROCESSING) {
      return {
        code: 'PROCESSING',
        icon: <ProcessingIcon
          color="#1f2328"
          className="icon"
          width={18}
          height={18}
        />,
        tooltip,
        id: newStatus,
        newStatus,
        oldStatus,
        shouldOpenDeliveredModal: false,
        shouldOpenReceivedModal: false
      }
    }

    if (newStatus == ORDER_STATUS.DELIVERY) {
      return {
        code: 'DELIVERY',
        icon: <TruckIcon
          className="icon"
          color="#1f2328"
          width={18}
          height={18}
        />,
        tooltip,
        id: newStatus,
        newStatus,
        oldStatus,
        shouldOpenDeliveredModal: true,
        shouldOpenReceivedModal: false
      }
    }

    if (newStatus == ORDER_STATUS.CUSTOMER_CONFIRM) {
      return {
        code: 'CONFIRM',
        icon: <PendingConfirmIcon className="icon" width={18} height={18} />,
        tooltip,
        id: newStatus,
        newStatus,
        oldStatus,
        shouldOpenDeliveredModal: false,
        shouldOpenReceivedModal: false
      }
    }

    if (newStatus == ORDER_STATUS.SUPPLIER_CONFIRM) {
      return {
        code: 'CONFIRM_SUPPLIER',
        icon: <PendingSupplierConfirmIcon
          className="icon"
          width={18}
          height={18}
        />,
        tooltip,
        id: newStatus,
        newStatus,
        oldStatus,
        shouldOpenDeliveredModal: false,
        shouldOpenReceivedModal: true
      }
    }

    if (newStatus == ORDER_STATUS.COMPLETE) {
      return {
        code: 'COMPLETE',
        icon: <CompleteIcon className="icon" width={18} height={18} />,
        tooltip,
        id: newStatus,
        newStatus,
        oldStatus,
        shouldOpenDeliveredModal: false,
        shouldOpenReceivedModal: true
      }
    }

  }

  return null
}

export const generateNextStepUpdateOrder = (currentStatus: number): NextUpdateStatusChainOrder | null | undefined => {
  const foundCurrentStatusIndex = FLOW_UPDATE_STATUS.findIndex(item => item === currentStatus)
  const oldStatus = currentStatus;


  // Kiểm tra tồn tại và không phải là phần tử cuối
  if (foundCurrentStatusIndex != -1 && foundCurrentStatusIndex != FLOW_UPDATE_STATUS.length - 1) {
    const newStatus = FLOW_UPDATE_STATUS[foundCurrentStatusIndex + 1]
    const id = newStatus
    const tooltip = ORDER_STATUS_TEXT[newStatus]


    if (newStatus == ORDER_STATUS.PENDING_EXECUTE) {
      return {
        code: 'PENDING_EXECUTE',
        icon: <PendingExecuteIcon className="icon" width={18} height={18} />,
        tooltip,
        id: newStatus,
        newStatus,
        oldStatus,
        shouldOpenDeliveredModal: false,
        shouldOpenReceivedModal: false
      }
    }

    if (newStatus == ORDER_STATUS.PROCESSING) {
      return {
        code: 'PROCESSING',
        icon: <ProcessingIcon
          color="#1f2328"
          className="icon"
          width={18}
          height={18}
        />,
        tooltip,
        id: newStatus,
        newStatus,
        oldStatus,
        shouldOpenDeliveredModal: false,
        shouldOpenReceivedModal: false
      }
    }

    if (newStatus == ORDER_STATUS.DELIVERY) {
      return {
        code: 'DELIVERY',
        icon: <TruckIcon
          className="icon"
          color="#1f2328"
          width={18}
          height={18}
        />,
        tooltip,
        id: newStatus,
        newStatus,
        oldStatus,
        shouldOpenDeliveredModal: true,
        shouldOpenReceivedModal: false
      }
    }

    if (newStatus == ORDER_STATUS.CUSTOMER_CONFIRM) {
      return {
        code: 'CONFIRM',
        icon: <PendingConfirmIcon className="icon" width={18} height={18} />,
        tooltip,
        id: newStatus,
        newStatus,
        oldStatus,
        shouldOpenDeliveredModal: false,
        shouldOpenReceivedModal: false
      }
    }

    if (newStatus == ORDER_STATUS.SUPPLIER_CONFIRM) {
      return {
        code: 'CONFIRM_SUPPLIER',
        icon: <PendingSupplierConfirmIcon
          className="icon"
          width={18}
          height={18}
        />,
        tooltip,
        id: newStatus,
        newStatus,
        oldStatus,
        shouldOpenDeliveredModal: false,
        shouldOpenReceivedModal: true
      }
    }

    if (newStatus == ORDER_STATUS.COMPLETE) {
      return {
        code: 'COMPLETE',
        icon: <CompleteIcon className="icon" width={18} height={18} />,
        tooltip,
        id: newStatus,
        newStatus,
        oldStatus,
        shouldOpenDeliveredModal: false,
        shouldOpenReceivedModal: true
      }
    }

  }

  return null
}

export const reorder = (list: any, startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

export const buildEntityShippingPolicyItemName = (entityShippingPolicyItem) => {
  const fromLocations: string[] = [];
  const toLocations: string[] = [];

  if (entityShippingPolicyItem.fromWard?.fullName) {
    fromLocations.push(entityShippingPolicyItem.fromWard?.fullName);
  }
  if (entityShippingPolicyItem.fromDistrict?.fullName) {
    fromLocations.push(entityShippingPolicyItem.fromDistrict?.fullName);
  }
  if (entityShippingPolicyItem.fromProvince?.fullName) {
    fromLocations.push(entityShippingPolicyItem.fromProvince?.fullName);
  }

  if (entityShippingPolicyItem.toWard?.fullName) {
    toLocations.push(entityShippingPolicyItem.toWard?.fullName);
  }
  if (entityShippingPolicyItem.toDistrict?.fullName) {
    toLocations.push(entityShippingPolicyItem.toDistrict?.fullName);
  }
  if (entityShippingPolicyItem.toProvince?.fullName) {
    toLocations.push(entityShippingPolicyItem.toProvince?.fullName);
  }

  return `${fromLocations.join(", ")} - ${toLocations.join(", ")}`;
};

export const checkPermission = ({
  module,
  groupFeature,
  feature,
  permissions = {},
}: {
  permissions: any;
  module?: string;
  groupFeature?: string;
  feature?: string;
}) => {

  if (module && groupFeature && feature) {
    return permissions[module]?.menus[groupFeature]?.menus[feature]?.value;
  }

  if (module && groupFeature) {
    return permissions[module]?.menus[groupFeature]?.value;
  }

  if (module) {
    return permissions[module]?.value;
  }

  return false;
};

export const genMenuType = ({ permissions = [] }: { permissions: any }) => {
  const newPermissions = permissions;
  delete newPermissions.settings;

  return Object.values(newPermissions)
    .map((item: any) => item.value)
    .filter((item: any) => item).length;
};

export const getCurrentTabIndex = () => {
  const time = new Date().getTime();

  const tabIndex = `TabIndex${time}`;
  return tabIndex;
};


/**
 *
 * @returns Lấy thông tin thiết bị
 */
export const getUserInfoComputer = () => {
  let OSName = 'Unknown';
  if (window.navigator.userAgent.indexOf('Windows NT 10.0') != -1) OSName = 'Windows 10';
  if (window.navigator.userAgent.indexOf('Windows NT 6.3') != -1) OSName = 'Windows 8.1';
  if (window.navigator.userAgent.indexOf('Windows NT 6.2') != -1) OSName = 'Windows 8';
  if (window.navigator.userAgent.indexOf('Windows NT 6.1') != -1) OSName = 'Windows 7';
  if (window.navigator.userAgent.indexOf('Windows NT 6.0') != -1) OSName = 'Windows Vista';
  if (window.navigator.userAgent.indexOf('Windows NT 5.1') != -1) OSName = 'Windows XP';
  if (window.navigator.userAgent.indexOf('Windows NT 5.0') != -1) OSName = 'Windows 2000';
  if (window.navigator.userAgent.indexOf('Mac') != -1) OSName = 'Mac/iOS';
  if (window.navigator.userAgent.indexOf('X11') != -1) OSName = 'UNIX';
  if (window.navigator.userAgent.indexOf('Linux') != -1) OSName = 'Linux';

  const isChrome = navigator.userAgent.indexOf('Chrome') > -1;
  const isSafari = navigator.userAgent.indexOf('Safari') > -1;
  const isFireFox = navigator.userAgent.indexOf('Firefox') != -1;
  const isOpera = (navigator.userAgent.indexOf('Opera') || navigator.userAgent.indexOf('OPR')) != -1;
  if (isOpera) {
    return `${OSName}-Opera`;
  }
  if (isFireFox) {
    return `${OSName}-Firefox`;
  }

  // let isIE = navigator.userAgent.indexOf("MSIE") > -1
  if (isSafari) {
    if (isChrome) return `${OSName}-Chrome`;
    return `${OSName}-Safari`;
  }
  if (!isChrome && !isSafari) {
    return `${OSName}-Safari`;
  }
  return `${OSName}-Unknown`;
};

export const formatSalePromotions = (promotion: any, unit?: any) => {
  const method = promotion?.productDiscountType == 2 ? 'giá' : 'giảm giá';
  let postfix =
    promotion?.productDiscountType == 1
      ? '%'
      : promotion?.productDiscountType == 2
        ? `đ/${!isEmpty(unit?.unitPrefix?.name)
          ? unit?.unitPrefix?.name
          : unit?.unitWeight == 1
            ? `${unit?.unitPostfix?.name}`
            : `${unit?.unitWeight} ${unit?.unitPostfix?.name}`
        }`
        : 'đ';
  let value =
    promotion?.productDiscountType == 0
      ? promotion?.customerProductDiscount || promotion?.productDiscount
      : promotion?.productDiscountType == 1
        ? promotion?.productDiscountRatio
        : promotion?.customerProductPrice || promotion?.productPrice;

  if (promotion.salePromotionType == 1) {
    if (promotion.salePromotionMethod == 5)
      return `Đơn ${formatMoney(promotion?.fromOrderTotalPrice)}đ ${method} ${formatMoney(value)}${postfix}`;

    if (promotion.salePromotionMethod == 3) {
      postfix =
        promotion?.orderDiscountType == 1
          ? '%'
          : 'đ';
      value =
        promotion?.orderDiscountType == 0
          ? promotion?.orderDiscount
          : promotion?.orderDiscountRatio;

      return `Đơn ${formatMoney(promotion?.fromOrderTotalPrice)}đ ${method} ${formatMoney(value)}${postfix}`;
    }

  }

  return `Mua ${!isEmpty(unit?.unitPrefix?.name)
    ? `${promotion?.customerFromQuantity || promotion?.fromQuantity} ${unit?.unitPrefix?.name}`
    : `${(promotion?.customerFromQuantity || promotion?.fromQuantity) * unit?.unitWeight} ${unit?.unitPostfix?.name}`
    } ${method} ${formatMoney(value)}${postfix}`;
};

export const formatPromotions = (promotion: any) => {
  if (promotion.type === PROMOTION_TYPE.RATE) {
    return `CK ${promotion?.rate}% (-
    ${formatMoney(promotion?.adjustment)}đ)`
  }

  return `CK ${formatMoney(promotion?.amount)}VNĐ (-
    ${formatMoney(promotion?.adjustment)}đ)`
};

export const applySalePromotions = (salePromotion: any) => {
  const value =
    salePromotion?.productDiscountType == 0
      ? salePromotion?.productDiscount
      : salePromotion?.productDiscountType == 1
        ? salePromotion?.productDiscountRatio
        : salePromotion?.productPrice;

  return salePromotion?.productDiscountType == 1 ? `${value}%` : undefined;
};

export const buildItemAdjustment = (orderItem) => {
  const salePromotion = (orderItem.adjustments ?? []).find(
    (item: any) => item.type === ADJUSTMENT_TYPE.ITEM_SALE_PROMOTION
  );
  const promotion = (orderItem.adjustments ?? []).find(
    (item: any) => item.type === ADJUSTMENT_TYPE.ITEM_PROMOTION
  );

  let discount = '';
  let percentDiscount;
  if (salePromotion) {
    percentDiscount = applySalePromotions(
      JSON.parse(salePromotion.info)
    );
    discount = formatMoney(orderItem.originPrice - orderItem.price);
  }

  if (promotion) {
    const formatPromotion = JSON.parse(promotion.info);
    if (formatPromotion.type === PROMOTION_TYPE.RATE) {
      percentDiscount = `${formatPromotion.rate}%`;
      discount = formatMoney(Math.floor(
        (orderItem.originPrice * formatPromotion.rate) / 100
      ));
    } else {
      discount = formatMoney(formatPromotion.amount);
    }
  }

  return { discount, percentDiscount };
}